import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import debounce from 'lodash/debounce';
import Filters from '../Filters/Filters';
import LoadingOverlay from '../Loading/LoadingOverlay';
import DataSelector from '../DataSelector/DataSelector';
import ExportActions from '../ExportActions/ExportActions';
import GradientLegend from '../MapControls/GradientLegend';
import MapLocation from '../MapControls/MapLocation';
import MapMinMax from '../MapControls/MapMinMax';
import MapLayer from '../MapControls/MapLayer';
import { getValueColor, getRateColor, getRateDiffColor, getScoreColor, getMapColor } from '../../utils/coloring';
import { formatNumber, formatDifference, formatRank, formatPercentile } from '../../utils/formatting';
import { getStateAbbreviation, getFullStateName, isStateScopedCityCountyAggregate } from '../../constants/locations';
import { getCrimeDisplayName } from '../../constants/crimes';
import { fetchLocationData } from '../../services/api';
import 'leaflet/dist/leaflet.css';
import './MapView.css';

function layerStateMatchesSelection(layerState, commaSplitStatePart) {
  if (layerState == null || commaSplitStatePart == null) return false;
  const a = String(layerState).trim();
  const b = String(commaSplitStatePart).trim();
  if (a === b) return true;
  const codeA = getStateAbbreviation(a) || a;
  const codeB = getStateAbbreviation(b) || b;
  return codeA === codeB;
}

const MAP_POPUP_LOCK_SVG = `<svg class="map-popup-lock-svg" xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>`;

function citySearchKeyVariants(feature) {
  if (!feature?.properties?.city || !feature?.properties?.state) return [];
  const c = feature.properties.city;
  const s = feature.properties.state;
  const abbr = getStateAbbreviation(s);
  return [`${c}, ${s}`.toLowerCase(), `${c}, ${abbr}`.toLowerCase()];
}

function getCountyOnSelectName(feature) {
  const fp = feature?.properties;
  if (!fp?.county || !fp?.state) return null;
  if (fp.county === 'Carson City' && fp.state === 'Nevada') {
    return `${fp.county}, ${fp.state}`;
  }
  if (fp.state === 'Louisiana') {
    return `${fp.county} Parish, ${fp.state}`;
  }
  if (fp.state === 'Alaska') {
    return `${fp.county} Borough, ${fp.state}`;
  }
  return `${fp.county} County, ${fp.state}`;
}

function countySearchKeyVariants(feature) {
  const name = getCountyOnSelectName(feature);
  if (!name) return [];
  const lower = name.toLowerCase();
  const idx = name.lastIndexOf(', ');
  if (idx === -1) return [lower];
  const stateFull = name.slice(idx + 2);
  const base = name.slice(0, idx);
  const abbr = getStateAbbreviation(stateFull);
  return Array.from(new Set([lower, `${base}, ${abbr}`.toLowerCase()]));
}

function mapFeatureVisitedKeys(feature, locationType) {
  const keys =
    locationType === 'city'
      ? citySearchKeyVariants(feature)
      : locationType === 'county'
        ? countySearchKeyVariants(feature)
        : [];
  const suffix = `::${locationType}`;
  return keys.map((k) => k + suffix);
}

function isMapFeatureVisited(feature, locationType, visitedSet) {
  if (!visitedSet?.has) return false;
  return mapFeatureVisitedKeys(feature, locationType).some((vk) => visitedSet.has(vk));
}

function mapFeatureMatchesSelected(feature, locationType, selectedName) {
  if (!selectedName) return false;
  const sn = selectedName.toLowerCase();
  const keys =
    locationType === 'city'
      ? citySearchKeyVariants(feature)
      : locationType === 'county'
        ? countySearchKeyVariants(feature)
        : [];
  return keys.some((k) => k === sn);
}

function shouldLockMapFeaturePopup(feature, gate, mapLayerType) {
  if (!gate?.atLimit || !feature?.properties) return false;
  if (mapLayerType !== 'city' && mapLayerType !== 'county') return false;
  if (!gate.visited || typeof gate.visited.has !== 'function') return false;
  if (gate.selectedType !== mapLayerType) return false;
  if (mapFeatureMatchesSelected(feature, mapLayerType, gate.selectedName)) return false;
  if (isMapFeatureVisited(feature, mapLayerType, gate.visited)) return false;
  return true;
}

const getDefaultRange = (tab) => {
  switch (tab) {
    case 'reports':
    case 'totals':
      return 'count';
    case 'trends':
      return 'trend_diff_pct';
    case 'rates':
      return 'rate';
    case 'scores':
      return 'score';
    default:
      return 'count';
  }
};

const getValueForRange = (feature, range, crimePrefix, activeTab, compareMode, rateViewMode, locationType) => {
  if (!feature?.properties) return null;

  const extractRankValue = (rank) => {
    if (typeof rank !== 'string') return rank;
    const match = rank.match(/\d+/);
    return match ? parseInt(match[0], 10) : rank;
  };

  const isCounty = feature.properties.county !== undefined;
  const isCity = feature.properties.city !== undefined;
  const isMetro = locationType === 'metro'; 
  const isCountyOrCity = isCounty || isCity;
  const compareType = isCountyOrCity ? (compareMode || 'national') : 'national';

  if (activeTab === 'rates' && (isCountyOrCity || isMetro) && rateViewMode) {
    const rateType = rateViewMode.rateType || 'county';
    const rateScope = rateViewMode.rateScope || 'state';
    
    let rankField, percentileField, avgRateField, rateDiffField, rateDiffPctField;
    
    if (isMetro && rateType === 'metro') {
      rankField = `${crimePrefix}_national_rate_rank`;
      percentileField = `${crimePrefix}_national_rate_percentile`;
      avgRateField = `${crimePrefix}_all_metros_rate`;
      rateDiffField = `${crimePrefix}_all_metros_rate_diff`;
      rateDiffPctField = `${crimePrefix}_all_metros_rate_diff_pct`;
    } else if (isMetro) {
      rankField = `${crimePrefix}_national_rate_rank`;
      percentileField = `${crimePrefix}_national_rate_percentile`;
      avgRateField = `${crimePrefix}_national_rate`;
      rateDiffField = `${crimePrefix}_national_rate_diff`;
      rateDiffPctField = `${crimePrefix}_national_rate_diff_pct`;
    } else if (rateType === 'county' || rateType === 'city') {
      const locationTypePrefix = rateType === 'county' ? 'all_counties' : 'all_cities';
      if (rateScope === 'state') {
        rankField = `${crimePrefix}_state_rate_rank`;
        percentileField = `${crimePrefix}_state_rate_percentile`;
        avgRateField = `${crimePrefix}_${locationTypePrefix}_state_rate`;
        rateDiffField = `${crimePrefix}_${locationTypePrefix}_state_rate_diff`;
        rateDiffPctField = `${crimePrefix}_${locationTypePrefix}_state_rate_diff_pct`;
      } else { 
        rankField = `${crimePrefix}_national_rate_rank`;
        percentileField = `${crimePrefix}_national_rate_percentile`;
        avgRateField = `${crimePrefix}_${locationTypePrefix}_national_rate`;
        rateDiffField = `${crimePrefix}_${locationTypePrefix}_national_rate_diff`;
        rateDiffPctField = `${crimePrefix}_${locationTypePrefix}_national_rate_diff_pct`;
      }
    } else { 
      if (rateScope === 'state') {
        rankField = `${crimePrefix}_state_rate_rank`;
        percentileField = `${crimePrefix}_state_rate_percentile`;
        avgRateField = `${crimePrefix}_state_rate`;
        rateDiffField = `${crimePrefix}_state_rate_diff`;
        rateDiffPctField = `${crimePrefix}_state_rate_diff_pct`;
      } else { 
        rankField = `${crimePrefix}_national_rate_rank`;
        percentileField = `${crimePrefix}_national_rate_percentile`;
        avgRateField = `${crimePrefix}_national_rate`;
        rateDiffField = `${crimePrefix}_national_rate_diff`;
        rateDiffPctField = `${crimePrefix}_national_rate_diff_pct`;
      }
    }
    
    switch (range) {
      case 'rate':
        return feature.properties[`${crimePrefix}_rate`];
      case 'national_rate':
        return feature.properties[avgRateField];
      case 'rate_diff':
        return feature.properties[rateDiffField];
      case 'rate_diff_pct':
        return feature.properties[rateDiffPctField];
      case 'rate_vs_national_rate':
        return feature.properties[rateDiffField];
      case 'rank':
        return extractRankValue(feature.properties[rankField]);
      case 'percentile':
        return feature.properties[percentileField];
      default:
        return null;
    }
  }
  
  if (activeTab === 'scores') {
    const getScoreFieldKey = (prefix) => {
      if (!isCountyOrCity) return `${prefix}_score`;
      
      return compareType === 'state' 
        ? `${prefix}_state_score` 
        : `${prefix}_national_score`;
    };
    
    const getScoreRankFieldKey = (prefix) => {
      if (!isCountyOrCity) return `${prefix}_score_rank`;
      
      return compareType === 'state' 
        ? `${prefix}_state_score_rank` 
        : `${prefix}_national_score_rank`;
    };
    
    const getCompositeFieldKey = (baseField) => {
      if (!isCountyOrCity) return baseField;
      
      return compareType === 'state'
        ? `state_${baseField}`
        : `national_${baseField}`;
    };
    
    switch (range) {
      case 'score':
        return feature.properties[getScoreFieldKey(crimePrefix)];
      case 'composite_score':
        return feature.properties[getCompositeFieldKey('composite_score')];
      case 'composite_score_rank':
        return extractRankValue(feature.properties[getCompositeFieldKey('composite_score_rank')]);
      case 'rank':
        return extractRankValue(feature.properties[getScoreRankFieldKey(crimePrefix)]);
      default:
        return null;
    }
  }

  switch (range) {
    case 'count':
      return feature.properties[`${crimePrefix}_count`];
    case 'count_avg':
      return feature.properties[`${crimePrefix}_${compareType}_count_avg`];
    case 'avg_diff':
      return feature.properties[`${crimePrefix}_${compareType}_count_avg_diff`];
    case 'avg_diff_pct':
      return feature.properties[`${crimePrefix}_${compareType}_count_avg_diff_pct`];
    case 'count_vs_avg':
      return feature.properties[`${crimePrefix}_${compareType}_count_avg_diff`];
    
    case 'prev_count':
      return feature.properties[`${crimePrefix}_count_prev`];
    case 'trend_diff':
      return feature.properties[`${crimePrefix}_count_prev_diff`];
    case 'trend_diff_pct':
      return feature.properties[`${crimePrefix}_count_prev_diff_pct`];
    case 'count_vs_prev':
      return feature.properties[`${crimePrefix}_count_prev_diff`];
    
    case 'rate':
      return feature.properties[`${crimePrefix}_rate`];
    case 'national_rate':
      return feature.properties[`${crimePrefix}_national_rate`];
    case 'rate_diff':
      return feature.properties[`${crimePrefix}_national_rate_diff`];
    case 'rate_diff_pct':
      return feature.properties[`${crimePrefix}_national_rate_diff_pct`];
    case 'rate_vs_national_rate':
      return feature.properties[`${crimePrefix}_national_rate_diff`];
    
    case 'rank':
      if (isCountyOrCity) {
        switch (activeTab) {
          case 'reports':
          case 'totals':
            return extractRankValue(feature.properties[`${crimePrefix}_${compareType}_count_rank`]);
          case 'trends':
            return extractRankValue(feature.properties[`${crimePrefix}_${compareType}_trend_rank`]);
          case 'rates':
            return extractRankValue(feature.properties[`${crimePrefix}_${compareType}_rate_rank`]);
          default:
            return null;
        }
      } else {
        switch (activeTab) {
          case 'reports':
          case 'totals':
            return extractRankValue(feature.properties[`${crimePrefix}_count_rank`]);
          case 'trends':
            return extractRankValue(feature.properties[`${crimePrefix}_trend_rank`]);
          case 'rates':
            return extractRankValue(feature.properties[`${crimePrefix}_rate_rank`]);
          default:
            return null;
        }
      }
    case 'percentile':
      if (isCountyOrCity) {
        switch (activeTab) {
          case 'reports':
          case 'totals':
            return feature.properties[`${crimePrefix}_${compareType}_count_percentile`];
          case 'trends':
            return feature.properties[`${crimePrefix}_${compareType}_trend_percentile`];
          case 'rates':
            return feature.properties[`${crimePrefix}_${compareType}_rate_percentile`];
          default:
            return null;
        }
      } else {
        switch (activeTab) {
          case 'reports':
          case 'totals':
            return feature.properties[`${crimePrefix}_count_percentile`];
          case 'trends':
            return feature.properties[`${crimePrefix}_trend_percentile`];
          case 'rates':
            return feature.properties[`${crimePrefix}_rate_percentile`];
          default:
            return null;
        }
      }
    default:
      return null;
  }
};

const getMinMaxValues = (locations, range, crimePrefix, activeTab, compareMode, rateViewMode, locationType) => {
  if (!locations?.length) return { min: 0, max: 0 };

  if (compareMode === 'national' && 
      locations.length > 0 && 
      (locations[0].county !== undefined || locations[0].city !== undefined)) {
    
    const isAllCountiesNationwide = locations.length > 0 && 
                                  !locations[0].state && 
                                  locations[0].name !== undefined;
    
    if (!isAllCountiesNationwide) {
      let minField = null;
      let maxField = null;
      
      if (activeTab === 'rates') {
        const useAllCounties = rateViewMode?.rateType === 'county';
        const useAllCities = rateViewMode?.rateType === 'city';
        const useAllMetros = rateViewMode?.rateType === 'metro';
        let prefix;
        
        if (useAllCounties) {
          prefix = `${crimePrefix}_all_counties_national`;
        } else if (useAllCities) {
          prefix = `${crimePrefix}_all_cities_national`;
        } else if (useAllMetros) {
          prefix = `${crimePrefix}_all_metros`; 
        } else {
          prefix = `${crimePrefix}_national`;
        }
        
        if (range === 'rate') {
          minField = `${crimePrefix}_rate_min`;
          maxField = `${crimePrefix}_rate_max`;
        } 
        else if (range === 'rate_diff' || range === 'rate_vs_national_rate') {
          minField = `${prefix}_rate_diff_min`;
          maxField = `${prefix}_rate_diff_max`;
        } 
        else if (range === 'rate_diff_pct') {
          minField = `${prefix}_rate_diff_pct_min`;
          maxField = `${prefix}_rate_diff_pct_max`;
        }
        else if (range === 'rank') {
          minField = `${crimePrefix}_national_rate_rank_min`;
          maxField = `${crimePrefix}_national_rate_rank_max`;
        }
      }
      else if (range === 'rank') {
        if (activeTab === 'trends') {
          minField = `${crimePrefix}_national_trend_rank_min`;
          maxField = `${crimePrefix}_national_trend_rank_max`;
        } else if (activeTab === 'scores') {
          minField = `${crimePrefix}_national_score_rank_min`;
          maxField = `${crimePrefix}_national_score_rank_max`;
        } else {
          minField = `${crimePrefix}_national_count_rank_min`;
          maxField = `${crimePrefix}_national_count_rank_max`;
        }
      }
      else if (range === 'count') {
        minField = `${crimePrefix}_count_min`;
        maxField = `${crimePrefix}_count_max`;
      } 
      else if (range === 'count_vs_avg' || range === 'avg_diff') {
        minField = `${crimePrefix}_national_count_avg_diff_min`;
        maxField = `${crimePrefix}_national_count_avg_diff_max`;
      } 
      else if (range === 'avg_diff_pct') {
        minField = `${crimePrefix}_national_count_avg_diff_pct_min`;
        maxField = `${crimePrefix}_national_count_avg_diff_pct_max`;
      } 
      else if (range === 'prev_count') {
        minField = `${crimePrefix}_count_prev_min`;
        maxField = `${crimePrefix}_count_prev_max`;
      } 
      else if (range === 'trend_diff' || range === 'count_vs_prev') {
        minField = `${crimePrefix}_count_prev_diff_min`;
        maxField = `${crimePrefix}_count_prev_diff_max`;
      } 
      else if (range === 'trend_diff_pct') {
        minField = `${crimePrefix}_count_prev_diff_pct_min`;
        maxField = `${crimePrefix}_count_prev_diff_pct_max`;
      } 
      else if (range === 'score') {
        minField = `${crimePrefix}_national_score_min`;
        maxField = `${crimePrefix}_national_score_max`;
      } 
      else if (range === 'composite_score') {
        minField = 'national_composite_score_min';
        maxField = 'national_composite_score_max';
      }
      else if (range === 'composite_score_rank') {
        minField = 'national_composite_score_rank_min';
        maxField = 'national_composite_score_rank_max';
      } 

      if (minField && maxField && 
          locations[0][minField] !== undefined && 
          locations[0][maxField] !== undefined) {
        return {
          min: Number(locations[0][minField]),
          max: Number(locations[0][maxField])
        };
      }
    }
  }

  const values = locations.map(location => 
    getValueForRange({ properties: location }, range, crimePrefix, activeTab, compareMode, rateViewMode, locationType)
  ).filter(val => val != null);
  
  return {
    min: Math.min(...values),
    max: Math.max(...values)
  };
};

const getEffectiveTab = (range) => {
  if (range === 'score' || range === 'composite_score') return 'scores';
  if (range === 'rate' || range === 'rate_diff' || range === 'rate_vs_national_rate') return 'rates';
  if (range.includes('diff') || range === 'trend_diff_pct') return 'trends';
  if (range === 'rank' || range === 'composite_score_rank') return 'rank';
  return 'reports';
};

const GeoJSONLayer = React.memo(({ 
  features, 
  stateFeatures,
  mapStyle, 
  locationTypeData,
  onLocationSelect,
  selectedLocation,
  selectedDateRange,
  hasPreviousData,
  hasData,
  onPopupOpen,
  isAnyMenuOpen,
  selectedSource,
  rateViewMode,
  getActiveCompareMode,
  hoveredLocation,
  setHoveredLocation,
  preventHoverUntilMouseMove,
  isMobile,
  isMapMobile,
  isTouchDevice,
  closePopupSignal,
  searchBlockedRef,
  mapSearchGateRef,
  userPlan,
  popupOpen,
  disableInteractions,
}) => {
  const [, setCurrentGeometryCity] = useState(null);
  const [, setCurrentPopupFeature] = useState(null);
  const [hoveredSharedCity, setHoveredSharedCity] = useState(null);
  const [, setIsStateTransitioning] = useState(false);

  const map = useMap();
  const layerRef = useRef();
  const layerMapRef = useRef(null);
  const clickedLayerRef = useRef(null);
  const clickInitiatedRef = useRef(false);
  const selectedLayerRef = useRef(null);
  const prevSelectionRef = useRef(null);
  const isTransitioning = useRef(false);
  const programmaticZoomRef = useRef(false);
  const lastFitBoundsLocationRef = useRef(null);
  const isProcessingClose = useRef(false);
  const hoverPopupRef = useRef(null);
  const hoverPopupContentRef = useRef(null);
  const cancelPopupOpenRef = useRef(false);
  const recenterOnPopupClose = useRef(false);
  const allCountiesCitiesPopupLayerRef = useRef(null);
  const hoverBorderOverlaysRef = useRef(new Map());
  const selectedBorderOverlayRef = useRef(null);
  const stateBoundariesLayerRef = useRef();
  const stateHoverPopupLayerRef = useRef(null);
  const stateZoomLevelRef = useRef({});
  const currentViewStateRef = useRef(null);
  const prevLocationInfoRef = useRef();
  const currentBoundsRef = useRef(null);

  useEffect(() => {
    if (!closePopupSignal) return;
    if (clickedLayerRef.current) {
      clickedLayerRef.current.closePopup();
      clickedLayerRef.current = null;
      onPopupOpen(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closePopupSignal]);

  const compareMode = getActiveCompareMode();

  const isNation = selectedLocation?.info?.type === 'nation';
  const isCounty = selectedLocation?.info?.type === 'county';
  const isCity = selectedLocation?.info?.type === 'city';
  const isMetro = selectedLocation?.info?.type === 'metro';
  const isAllCountiesNationwide = isCounty && selectedLocation?.info?.name === 'All Counties';
  const isAllCitiesNationwide = isCity && selectedLocation?.info?.name === 'All Cities';
  const isAllMetrosNationwide = isMetro && selectedLocation?.info?.name === 'All Metros';
  const isAllCountiesInState = selectedLocation.info.type === 'county' && 
                              selectedLocation.info.name.startsWith('All ') && 
                              selectedLocation.info.name.endsWith(' Counties') &&
                              selectedLocation.info.name !== 'All Counties';
  const isAllCitiesInState = selectedLocation.info.type === 'city' && 
                            selectedLocation.info.name.startsWith('All ') && 
                            selectedLocation.info.name.endsWith(' Cities') &&
                            selectedLocation.info.name !== 'All Cities';

  const locationType = selectedLocation.info?.type?.charAt(0).toUpperCase() + selectedLocation.info?.type?.slice(1);
  const locTypeString = selectedLocation?.info?.type || '';
  const effectiveCrime = locationTypeData.crime || mapStyle.crime;
  const crimeName = getCrimeDisplayName(effectiveCrime, selectedSource);
  const crimePrefix = effectiveCrime.toLowerCase();

  const zoomLevel = isMapMobile ? 3 : 4;
  const centerLat = isMapMobile ? 37 : 39;
  const centerLong = isMapMobile ? -96 : -97;

  const getMapPadding = useCallback(() => [10, isMapMobile ? 65 : isMobile ? 95 : 80], [isMapMobile, isMobile]);
  const getPopupPadding = useCallback(() => [54, isMapMobile ? 65: isMobile ? 105 : 80], [isMapMobile, isMobile]);

  const createSpecialBoundsForState = (stateName, layerRef) => {
    if (!layerRef.current) return null;
    
    if (stateName === 'Alaska' || stateName === 'AK') {
      return L.latLngBounds(
        L.latLng(51.0, -130.0),  
        L.latLng(71.5, -170.0) 
      );
    }
    
    if (stateName === 'Hawaii' || stateName === 'HI') {
      return L.latLngBounds(
        L.latLng(18.5, -155.0), 
        L.latLng(22.5, -160.0)
      );
    }
    
    const stateFeatures = [];
    layerRef.current.eachLayer((layer) => {
      const featureState = layer.feature?.properties?.state;
      if (layerStateMatchesSelection(featureState, stateName)) {
        stateFeatures.push(layer);
      }
    });
    
    if (stateFeatures.length > 0) {
      const stateBounds = L.latLngBounds([]);
      stateFeatures.forEach(feature => {
        stateBounds.extend(feature.getBounds());
      });
      return stateBounds;
    }
    
    return null;
  };
  
  const zoomToStateBounds = useCallback((selectedLocation) => {
    if (!selectedLocation?.info?.type || !layerRef.current) return false;

    let stateName;
  
    if ((selectedLocation.info.name.startsWith('All ') && selectedLocation.info.name.endsWith(' Counties')) ||
        (selectedLocation.info.name.startsWith('All ') && selectedLocation.info.name.endsWith(' Cities'))) {
      let stateCode;
      if (selectedLocation.info.name.endsWith(' Counties')) {
        stateCode = selectedLocation.info.name.replace('All ', '').replace(' Counties', '');
      } else {
        stateCode = selectedLocation.info.name.replace('All ', '').replace(' Cities', '');
      }
      stateName = stateCode.length === 2 ? getFullStateName(stateCode) : stateCode;
      
      if (!stateName) {
        stateName = selectedLocation.info.state || selectedLocation.info.stateAbbr;
      }
    } else if (selectedLocation.info.name.includes(',')) {
      stateName = selectedLocation.info.name.split(',')[1]?.trim();
    } else if (selectedLocation.info.state) {
      stateName = selectedLocation.info.state;
    } else if (selectedLocation.info.stateAbbr) {
      stateName = selectedLocation.info.stateAbbr;
    }
  
    if (!stateName) return false;
  
    const bounds = createSpecialBoundsForState(stateName, layerRef);
    
    if (!bounds) {
      return false;
    }

    setIsStateTransitioning(true);
  
    map.fitBounds(bounds, {
      padding: getMapPadding(),
      animate: true,
      duration: 1
    });

    currentBoundsRef.current = true;

    setIsStateTransitioning(false);

    const stateKey = getStateAbbreviation(stateName) || stateName;
    if (stateZoomLevelRef.current[stateKey] === undefined) {
      map.once('zoomend', () => {
        stateZoomLevelRef.current[stateKey] = map.getZoom();
      });
    }
    
    return true;
  }, [map, getMapPadding]);

  const isPointInPolygon = useCallback((point, polygon) => {
    const x = point.lng;
    const y = point.lat;
    let inside = false;
  
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i][0]; 
      const yi = polygon[i][1]; 
      const xj = polygon[j][0];
      const yj = polygon[j][1];
  
      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
  
    return inside;
  }, []);

  const getClickedCity = useCallback((clickEvent, feature) => {
    if (!feature.properties.geometry_cities || feature.properties.geometry_cities.length <= 1) {
      return null;
    }
  
    if (!clickEvent || !feature.geometry || feature.geometry.type !== 'GeometryCollection') {
      return null;
    }
  
    const clickLatLng = clickEvent.latlng || clickEvent;
    if (!clickLatLng || !clickLatLng.lat || !clickLatLng.lng) {
      return null;
    }
  
    const geometries = feature.geometry.geometries;
    const geometryCities = feature.properties.geometry_cities;
  
    for (let i = geometries.length - 1; i >= 0; i--) { 
      if (i >= geometryCities.length) continue; 

      const geometry = geometries[i];
      
      try {
        if (geometry.type === 'Polygon') {
          if (isPointInPolygon(clickLatLng, geometry.coordinates[0])) {
            return geometryCities[i]; 
          }
        } else if (geometry.type === 'MultiPolygon') {
          for (const polygon of geometry.coordinates) {
            if (isPointInPolygon(clickLatLng, polygon[0])) {
              return geometryCities[i]; 
            }
          }
        }
      } catch (error) {
        console.warn('Error checking geometry for click detection:', error);
      }
    }
    
    return null;
  }, [isPointInPolygon]);

  useEffect(() => {
    if (!map || (selectedLocation?.info?.type === 'county' || selectedLocation?.info?.type === 'city' || 
                selectedLocation?.info?.type === 'metro')) return;

    if ((selectedLocation?.info?.type !== 'county' && selectedLocation?.info?.type !== 'city') && 
        (selectedLocation?.info?.name === 'Alaska' || selectedLocation?.info?.name === 'AK' ||
        selectedLocation?.info?.name === 'Hawaii' || selectedLocation?.info?.name === 'HI')) {
      map.setView([52, -125], isMapMobile ? 2 : 3, {
        animate: true,
        duration: 1
      });
      return;
    }
  
    map.setView([centerLat, centerLong], zoomLevel, {
      animate: true,
      duration: 1
    });
  }, [
    map, 
    selectedLocation?.info?.type, 
    selectedLocation?.info?.name, 
    isMapMobile,
    centerLat, 
    centerLong, 
    zoomLevel
  ]);

  useEffect(() => {
    if (!map || !selectedLocation?.info) return;
    
    const isNewSelection = prevSelectionRef.current !== selectedLocation.info.name;
    
    if ((isAllMetrosNationwide || isAllCountiesNationwide || isAllCitiesNationwide) && isNewSelection) {
      map.setView([centerLat, centerLong], zoomLevel, {
        animate: true,
        duration: 1
      });      
    }
    
    prevSelectionRef.current = selectedLocation.info.name;
  }, [
    map, 
    selectedLocation,
    isAllMetrosNationwide,
    isAllCountiesNationwide,
    isAllCitiesNationwide,
    centerLat, 
    centerLong, 
    zoomLevel
  ]);

  useEffect(() => {
    prevLocationInfoRef.current = selectedLocation.info;
  }, [selectedLocation.info]);

  useLayoutEffect(() => {
    lastFitBoundsLocationRef.current = null;
  }, [selectedLocation?.info?.name, selectedLocation?.info?.type]);

  useLayoutEffect(() => {
    if (!layerRef.current || !map || (selectedLocation?.info?.type !== 'county' && selectedLocation?.info?.type !== 'city' && 
        selectedLocation?.info?.type !== 'metro') || !selectedLocation?.info?.name) return;

    const locationKey = `${selectedLocation.info.name}__${selectedLocation.info.type}`;
    if (lastFitBoundsLocationRef.current === locationKey) return;

    if (selectedLocation.info?.clickInitiated && selectedLocation.info.type !== 'metro') {
      return;
    }

    const startProgrammaticZoom = () => {
      programmaticZoomRef.current = true;
      map.once('moveend', () => { programmaticZoomRef.current = false; });
    };

    if (isAllCountiesInState || isAllCitiesInState) {
      currentViewStateRef.current = selectedLocation.info.state;
      startProgrammaticZoom();
      if (zoomToStateBounds(selectedLocation)) {
        lastFitBoundsLocationRef.current = locationKey;
      } else {
        programmaticZoomRef.current = false;
      }
    } else {
      let locationName, stateName, cleanedLocationName;
      
      if (selectedLocation.info.type === 'county') {
        [locationName, stateName] = selectedLocation.info.name.split(',').map(s => s.trim());
        cleanedLocationName = locationName.replace(/ County$/, '').replace(/ Parish$/, '').replace(/ Borough$/, '');
      } else if (selectedLocation.info.type === 'city') {
        [locationName, stateName] = selectedLocation.info.name.split(',').map(s => s.trim());
        cleanedLocationName = locationName;
      } else if (selectedLocation.info.type === 'metro') {
        locationName = selectedLocation.info.name;
      }
  
      let selectedFeature = null;
      layerRef.current.eachLayer((layer) => {
        const properties = layer.feature?.properties;

        if (selectedLocation.info.type === 'metro') {
          if (properties?.name === selectedLocation.info.name) {
            selectedFeature = layer;
          }
        } else {
          const locationField = selectedLocation.info.type === 'county' ? 'county' : 'city';
          const isNameMatch = properties?.[locationField] === cleanedLocationName;
          const isConstituentMatch = properties?.constituent_cities && 
                                    Array.isArray(properties.constituent_cities) && 
                                    properties.constituent_cities.includes(cleanedLocationName);

          if (
            (isNameMatch || isConstituentMatch) &&
            layerStateMatchesSelection(properties?.state, stateName)
          ) {
            selectedFeature = layer;
          }
        }
      });
  
      if (selectedFeature) {
        if (selectedLocation.info.type === 'city') {
          currentViewStateRef.current = null;

          const locationBounds = selectedFeature.getBounds();
          const shouldAnimate = selectedLocation.info?.clickInitiated || clickInitiatedRef.current;

          startProgrammaticZoom();
          map.fitBounds(locationBounds, {
            padding: getMapPadding(),
            maxZoom: 12,
            animate: shouldAnimate,
            duration: shouldAnimate ? 1 : 0
          });
          lastFitBoundsLocationRef.current = locationKey;
        } else if (selectedLocation.info.type === 'county') {
          const prevInfo = prevLocationInfoRef.current;
          const [, stateName] = selectedLocation.info.name.split(',').map(s => s.trim());

          const isNavigatingBetweenCounties =
            prevInfo?.type === 'county' &&
            currentViewStateRef.current === stateName;

          if (isNavigatingBetweenCounties) {
          } else {
            startProgrammaticZoom();
            if (zoomToStateBounds(selectedLocation)) {
              map.once('zoomend', () => {
                selectedFeature.bringToFront();
              });
            } else {
              const locationBounds = selectedFeature.getBounds();
              map.fitBounds(locationBounds, {
                padding: getMapPadding(),
                maxZoom: 9,
                animate: true,
                duration: 1
              });
            }
          }
          currentViewStateRef.current = stateName;
          lastFitBoundsLocationRef.current = locationKey;
        } else if (selectedLocation.info.type === 'metro') {
          currentViewStateRef.current = null;

          const locationBounds = selectedFeature.getBounds();
          const shouldAnimate = selectedLocation.info?.clickInitiated || clickInitiatedRef.current;

          startProgrammaticZoom();
          map.fitBounds(locationBounds, {
            padding: getMapPadding(),
            maxZoom: 9,
            animate: shouldAnimate,
            duration: shouldAnimate ? 1 : 0
          });
          lastFitBoundsLocationRef.current = locationKey;
        }
      } else if (isAllMetrosNationwide || isAllCitiesNationwide || isAllCountiesNationwide) {
        lastFitBoundsLocationRef.current = locationKey;
      }
    }
  }, [
    map,
    selectedLocation,
    isAllMetrosNationwide,
    isAllCitiesNationwide,
    isAllCountiesNationwide,
    isAllCountiesInState,
    isAllCitiesInState,
    layerRef, 
    zoomToStateBounds, 
    getMapPadding,
    locationTypeData.data
  ]);

  useEffect(() => {
    if (!map) return;
  
    const handleResize = () => {
      map.invalidateSize();
      
      if (isAllCountiesInState || isAllCitiesInState) {
        zoomToStateBounds(selectedLocation);
      } else if ((selectedLocation?.info?.type !== 'county' && selectedLocation?.info?.type !== 'city') && 
        (selectedLocation?.info?.name === 'Alaska' || selectedLocation?.info?.name === 'AK' ||
        selectedLocation?.info?.name === 'Hawaii' || selectedLocation?.info?.name === 'HI')) {
        map.setView([52, -125], isMapMobile ? 2 : 3, {
          animate: true,
          duration: 1
        });
      } else if (selectedLocation?.info?.type === 'city' && !isAllCitiesNationwide) {
        const [locationName, stateName] = selectedLocation.info.name.split(',').map(s => s.trim());
        const cleanedLocationName = locationName;
        
        let selectedFeature = null;
        layerRef.current?.eachLayer((layer) => {
          if (
            layer.feature?.properties?.city === cleanedLocationName &&
            layerStateMatchesSelection(layer.feature?.properties?.state, stateName)
          ) {
            selectedFeature = layer;
          }
        });
        
        if (selectedFeature) {
          const locationBounds = selectedFeature.getBounds();
          map.fitBounds(locationBounds, {
            padding: getMapPadding(),
            maxZoom: 12,
            animate: true,
            duration: 1
          });
        }
      } else if (selectedLocation?.info?.type === 'county' && !isAllCountiesNationwide) {
        if (!zoomToStateBounds(selectedLocation)) {
          return;
        }
      } else if (selectedLocation?.info?.type === 'metro' && !isAllMetrosNationwide) {
        const metroName = selectedLocation.info.name;
        
        let selectedFeature = null;
        layerRef.current?.eachLayer((layer) => {
          if (layer.feature?.properties?.name === metroName) {
            selectedFeature = layer;
          }
        });

        if (selectedFeature) {
          const locationBounds = selectedFeature.getBounds();
          map.fitBounds(locationBounds, {
            padding: getMapPadding(),
            maxZoom: 9,
            animate: true,
            duration: 1
          });
        }
      } else {
        map.setView([centerLat, centerLong], zoomLevel, {
          animate: true,
          duration: 1
        });
      }
    };
  
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [
    map, 
    isAllCountiesInState,
    isAllCitiesInState, 
    isAllCountiesNationwide,
    isAllCitiesNationwide,
    isAllMetrosNationwide,
    zoomToStateBounds, 
    selectedLocation, 
    isMapMobile, 
    centerLat, 
    centerLong, 
    zoomLevel,
    layerRef,
    getMapPadding
  ]);

  useEffect(() => {
    const handleOrientationOrResize = () => {
      if (clickedLayerRef.current) {
        clickedLayerRef.current.closePopup();
        clickedLayerRef.current = null;
      }
    };
  
    window.addEventListener('resize', handleOrientationOrResize);
    window.addEventListener('orientationchange', handleOrientationOrResize);
  
    return () => {
      window.removeEventListener('resize', handleOrientationOrResize);
      window.removeEventListener('orientationchange', handleOrientationOrResize);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (e.target.closest('.breadcrumb-link')) {
        return;
      }
  
      cancelPopupOpenRef.current = true;
  
      const outsideMapChrome =
        !e.target.closest('.map-container') ||
        e.target.closest('.comparison-menu') ||
        e.target.closest('.comparison-menu-button');

      const layerPopup = clickedLayerRef.current?.getPopup?.();
      const shouldDismiss =
        outsideMapChrome && (popupOpen || !!layerPopup);

      if (!shouldDismiss) {
        return;
      }

      if (clickedLayerRef.current) {
        const popup = clickedLayerRef.current.getPopup();
        if (popup) {
          popup.options.autoPan = false;
          popup.options.autoPanPadding = null;
          clickedLayerRef.current.closePopup();
        }
        clickedLayerRef.current = null;
      }

      map?.closePopup();
      onPopupOpen(false);
      recenterOnPopupClose.current = false;

      if (map && layerRef.current) {
        if (isAllCountiesInState || isAllCitiesInState) {
          zoomToStateBounds(selectedLocation);
        } else if ((selectedLocation?.info?.type !== 'county' && selectedLocation?.info?.type !== 'city') &&
          (selectedLocation?.info?.name === 'Alaska' || selectedLocation?.info?.name === 'AK' ||
          selectedLocation?.info?.name === 'Hawaii' || selectedLocation?.info?.name === 'HI')) {
          map.setView([52, -125], isMapMobile ? 2 : 3, {
            animate: true,
            duration: 1
          });
        } else if (selectedLocation?.info?.type === 'city' && !isAllCitiesNationwide) {
          const [locationName, stateName] = selectedLocation.info.name.split(',').map(s => s.trim());
          const cleanedLocationName = locationName;

          let selectedFeature = null;
          layerRef.current.eachLayer((layer) => {
            if (
              layer.feature?.properties?.city === cleanedLocationName &&
              layerStateMatchesSelection(layer.feature?.properties?.state, stateName)
            ) {
              selectedFeature = layer;
            }
          });

          if (selectedFeature) {
            const locationBounds = selectedFeature.getBounds();
            map.fitBounds(locationBounds, {
              padding: getMapPadding(),
              maxZoom: 12,
              animate: true,
              duration: 1
            });
          }
        } else if (selectedLocation?.info?.type === 'county' && !isAllCountiesNationwide) {
          if (!zoomToStateBounds(selectedLocation)) {
            map.setView([centerLat, centerLong], zoomLevel, {
              animate: true,
              duration: 1
            });
          }
        } else if (selectedLocation?.info?.type === 'metro' && !isAllMetrosNationwide) {
          const metroName = selectedLocation.info.name;

          let selectedFeature = null;
          layerRef.current?.eachLayer((layer) => {
            if (layer.feature?.properties?.name === metroName) {
              selectedFeature = layer;
            }
          });

          if (selectedFeature) {
            const locationBounds = selectedFeature.getBounds();
            map.fitBounds(locationBounds, {
              padding: getMapPadding(),
              maxZoom: 9,
              animate: true,
              duration: 1
            });
          }
        } else {
          map.setView([centerLat, centerLong], zoomLevel, {
            animate: true,
            duration: 1
          });
        }
      }
    };
  
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [
    map,
    layerRef,
    isAllCountiesInState,
    isAllCitiesInState,
    isAllCountiesNationwide,
    isAllCitiesNationwide,
    isAllMetrosNationwide,
    selectedLocation,
    isMapMobile,
    zoomToStateBounds,
    centerLat,
    centerLong,
    zoomLevel,
    onPopupOpen,
    getMapPadding,
    popupOpen,
  ]);

  useEffect(() => {
    if (!map) return;
  
    const handleMapClick = (e) => {
      if (e.originalEvent && e.originalEvent._stopped) {
        return;
      }
      
      preventHoverUntilMouseMove.current = true;
      
      hoverBorderOverlaysRef.current.forEach((borderLayer) => {
        if (map.hasLayer(borderLayer)) {
          map.removeLayer(borderLayer);
        }
      });
      hoverBorderOverlaysRef.current.clear();
      
      if (clickedLayerRef.current) {
        const popup = clickedLayerRef.current.getPopup();
        if (popup) {
          clickedLayerRef.current.closePopup();
          clickedLayerRef.current = null;
          popup.options.autoPan = false;
          popup.options.autoPanPadding = null;
          onPopupOpen(false);
        }
      }

      if (!recenterOnPopupClose.current) {
        return;
      }      
      recenterOnPopupClose.current = false;
  
      if (isAllCountiesInState || isAllCitiesInState) {
        zoomToStateBounds(selectedLocation);
        return;
      }
  
      if ((selectedLocation?.info?.type !== 'county' && selectedLocation?.info?.type !== 'city') && 
          (selectedLocation?.info?.name === 'Alaska' || selectedLocation?.info?.name === 'AK' ||
          selectedLocation?.info?.name === 'Hawaii' || selectedLocation?.info?.name === 'HI')) {
        map.setView([52, -125], isMapMobile ? 2 : 3, {
          animate: true,
          duration: 1
        });
        return;
      }
  
      if (selectedLocation?.info?.type === 'city' && !isAllCitiesNationwide) {
        const [locationName, stateName] = selectedLocation.info.name.split(',').map(s => s.trim());
        const cleanedLocationName = locationName;
        
        let selectedFeature = null;
        layerRef.current?.eachLayer((layer) => {
          if (
            layer.feature?.properties?.city === cleanedLocationName &&
            layerStateMatchesSelection(layer.feature?.properties?.state, stateName)
          ) {
            selectedFeature = layer;
          }
        });
        
        if (selectedFeature) {
          const locationBounds = selectedFeature.getBounds();
          map.fitBounds(locationBounds, {
            padding: getMapPadding(),
            maxZoom: 12,
            animate: true,
            duration: 1
          });
        }
        return;
      }
  
      if ((selectedLocation?.info?.type === 'county' && !isAllCountiesNationwide) && layerRef.current) {
        if (!zoomToStateBounds(selectedLocation)) {
          map.setView([centerLat, centerLong], zoomLevel, {
            animate: true,
            duration: 1
          });
        } 
        return;
      }

      if (selectedLocation?.info?.type === 'metro' && !isAllMetrosNationwide) {
        const metroName = selectedLocation.info.name;
        
        let selectedFeature = null;
        layerRef.current?.eachLayer((layer) => {
          if (layer.feature?.properties?.name === metroName) {
            selectedFeature = layer;
          }
        });
        
        if (selectedFeature) {
          const locationBounds = selectedFeature.getBounds();
          map.fitBounds(locationBounds, {
            padding: getMapPadding(),
            maxZoom: 9,
            animate: true,
            duration: 1
          });
        } 
        return; 
      }

      map.setView([centerLat, centerLong], zoomLevel, {
        animate: true,
        duration: 1
      });
    };
  
    const handleZoom = () => {
      if (clickedLayerRef.current && !isTransitioning.current && !programmaticZoomRef.current) {
        const popup = clickedLayerRef.current.getPopup();
        if (popup) {
          clickedLayerRef.current.closePopup();
          clickedLayerRef.current = null;
          popup.options.autoPan = false;
          popup.options.autoPanPadding = null;
          onPopupOpen(false);
          recenterOnPopupClose.current = false;
        }
      }
    };
  
    map.on('click', handleMapClick);
    map.on('zoomstart', handleZoom);
    map.on('zoom', handleZoom);
  
    return () => {
      map.off('click', handleMapClick);
      map.off('zoomstart', handleZoom);
      map.off('zoom', handleZoom);
    };
  }, [
    map,
    preventHoverUntilMouseMove,
    layerRef,
    isAllCountiesInState,
    isAllCitiesInState,
    isAllCountiesNationwide,
    isAllCitiesNationwide,
    isAllMetrosNationwide,
    selectedLocation,
    isMapMobile,
    zoomToStateBounds,
    centerLat,
    centerLong,
    zoomLevel,
    onPopupOpen,
    getMapPadding
  ]);
  
  useEffect(() => {
    const handleFilterClick = (e) => {
      if (e.target.closest('.filter-panel') || 
          e.target.closest('.filter-menu') || 
          e.target.closest('.filter-select') ||
          e.target.closest('.dropdown-trigger') ||
          e.target.closest('.custom-dropdown')) {
        
        if (clickedLayerRef.current) {
          const popup = clickedLayerRef.current.getPopup();
          if (popup) {
            clickedLayerRef.current.closePopup();
            clickedLayerRef.current = null;
            popup.options.autoPan = false;
            popup.options.autoPanPadding = null;
            onPopupOpen(false);
          }
        }
        
        if (layerMapRef.current) {
          layerMapRef.current.fire('click');
        }
      }
    };
  
    document.addEventListener('mousedown', handleFilterClick);
    return () => document.removeEventListener('mousedown', handleFilterClick);
  }, [onPopupOpen]);

  useEffect(() => {
    if (map) {
      layerMapRef.current = map;
    }
  }, [map]);

  const generatePopupContent = useCallback((feature, clickEvent = null) => {
    if (!feature?.properties) return '';
  
    let stateAbbr = '';
    if (isCounty || isCity) {
      if (feature.properties && feature.properties.state) {
        stateAbbr = getStateAbbreviation(feature.properties.state);
      } else if (selectedLocation?.info?.state) {
        stateAbbr = getStateAbbreviation(selectedLocation.info.state);
      } else if (selectedLocation?.info?.stateAbbr) {
        stateAbbr = selectedLocation.info.stateAbbr;
      }
    }
      
    if (isAllCountiesNationwide || isAllCitiesNationwide) {
      const stateName = feature.properties.name;
      const isDC = isAllCountiesNationwide && stateName === 'District of Columbia';
  
      if (isDC) {
        return `
          <div class="popup-content">
            <div class="location-name" style="text-align: center;">
              ${stateName}
            </div>
            <div class="state-click-hint" style="font-size: 12px;">
              No County Data Available
            </div>
          </div>
        `;
      }
      
      return `
        <div class="popup-content">
          <div class="location-name" style="text-align: center;">
            ${stateName}
          </div>
          <div class="state-click-hint" style="font-size: 12px;">
            Click to View All ${isCounty ? 'Counties' : 'Cities'}
          </div>
        </div>
      `;
    }
  
    const isCombinedCity = feature.properties.constituent_cities && feature.properties.constituent_cities.length > 0;
    const clickedCity = clickEvent ? getClickedCity(clickEvent, feature) : null;
  
    let locationNameHtml = '';
    
    if (isCombinedCity) {
      const mainCityName = `${feature.properties.city}, ${getStateAbbreviation(feature.properties.state)}`
      
      locationNameHtml = `
        <div class="location-name">
          ${mainCityName}
          ${clickedCity ? `<div class="specific-jurisdiction">(${clickedCity})</div>` : '' }
        </div>
      `;
    } else {
      locationNameHtml = `
        <div class="location-name">
          ${isCounty ? 
            (feature.properties.state === 'Alaska') ?
              (() => {
                const county = feature.properties.county;
                if (!county) return '';
                
                const alaskaSuffixes = {
                  'Aleutians East': 'Borough',
                  'Aleutians West': 'Census Area',
                  'Anchorage': 'Municipality',
                  'Bethel': 'Census Area',
                  'Bristol Bay': 'Borough',
                  'Chugach': 'Census Area',
                  'Copper River': 'Census Area',
                  'Denali': 'Borough',
                  'Dillingham': 'Census Area',
                  'Fairbanks North Star': 'Borough',
                  'Haines': 'Borough',
                  'Hoonah-Angoon': 'Census Area',
                  'Juneau': 'City and Borough',
                  'Kenai Peninsula': 'Borough',
                  'Ketchikan Gateway': 'Borough',
                  'Kodiak Island': 'Borough',
                  'Kusilvak': 'Census Area',
                  'Lake and Peninsula': 'Borough',
                  'Matanuska-Susitna': 'Borough',
                  'Nome': 'Census Area',
                  'North Slope': 'Borough',
                  'Northwest Arctic': 'Borough',
                  'Petersburg': 'Borough',
                  'Prince of Wales-Hyder': 'Census Area',
                  'Sitka': 'City and Borough',
                  'Skagway': 'Municipality',
                  'Southeast Fairbanks': 'Census Area',
                  'Wrangell': 'City and Borough',
                  'Yakutat': 'City and Borough',
                  'Yukon-Koyukuk': 'Census Area'
                };
                
                for (const [baseName, suffix] of Object.entries(alaskaSuffixes)) {
                  if (county === baseName || county.startsWith(baseName)) {
                    return `${baseName} ${suffix}, ${getStateAbbreviation(feature.properties.state)}`;
                  }
                }
                return `${county}, ${getStateAbbreviation(feature.properties.state)}`;
              })() :
            (feature.properties.state === 'Virginia') ?
              (() => {
                const county = feature.properties.county;
                
                const citiesWithCityInName = ['Fairfax City', 'Franklin City', 'Richmond City', 'Roanoke City'];
                
                const citiesWithoutCityInName = [
                  'Alexandria', 'Bristol', 'Buena Vista', 'Charlottesville', 
                  'Chesapeake', 'Colonial Heights', 'Covington', 'Danville', 
                  'Emporia', 'Falls Church', 'Fredericksburg', 'Galax', 
                  'Hampton', 'Harrisonburg', 'Hopewell', 'Lexington', 
                  'Lynchburg', 'Manassas', 'Manassas Park', 'Martinsville', 
                  'Newport News', 'Norfolk', 'Norton', 'Petersburg', 
                  'Poquoson', 'Portsmouth', 'Radford', 'Salem', 
                  'Staunton', 'Suffolk', 'Virginia Beach', 'Waynesboro', 
                  'Williamsburg', 'Winchester'
                ];
                
                if (citiesWithCityInName.includes(county)) {
                  return `${county}, ${getStateAbbreviation(feature.properties.state)}`;
                }
                
                if (citiesWithoutCityInName.includes(county)) {
                  return `${county} City, ${getStateAbbreviation(feature.properties.state)}`;
                }
                
                return `${county} County, ${getStateAbbreviation(feature.properties.state)}`;
              })() :
            (feature.properties.state === 'Connecticut') ?
              `${feature.properties.county} Planning Region, ${getStateAbbreviation(feature.properties.state)}` :
            (feature.properties.county === 'Carson City' && feature.properties.state === 'Nevada') ||
            (feature.properties.county === 'Baltimore City' && feature.properties.state === 'Maryland') ||
            (feature.properties.county === 'St. Louis City' && feature.properties.state === 'Missouri') ?
              `${feature.properties.county}, ${getStateAbbreviation(feature.properties.state)}` :
              `${feature.properties.county} ${feature.properties.state === 'Louisiana' ? 'Parish' : 'County'}, 
              ${getStateAbbreviation(feature.properties.state)}`
          : isCity ?
              `${feature.properties.city}, ${getStateAbbreviation(feature.properties.state)}`
          : feature.properties.name}       
        </div>
      `;
    }

    const gateState = mapSearchGateRef?.current?.getState?.();
    const mapLayerKind = isCity ? 'city' : isCounty ? 'county' : null;
    if (
      gateState &&
      mapLayerKind &&
      !isAllCountiesNationwide &&
      !isAllCitiesNationwide &&
      !isNation &&
      !isMetro &&
      shouldLockMapFeaturePopup(feature, gateState, mapLayerKind)
    ) {
      return `
        <div class="popup-content map-popup--locked">
          ${locationNameHtml}
          <div class="date-range">
            ${selectedDateRange}
          </div>
          <div class="map-popup-limit-stack">
            <div class="map-popup-limit-icon-wrap">${MAP_POPUP_LOCK_SVG}</div>
            <h3 class="map-popup-limit-title">${userPlan === 'free' ? 'Search Limit Reached' : 'Monthly Search Limit Reached'}</h3>
            <p class="map-popup-limit-body">${userPlan === 'free' ? "You've used all 5 location searches to get started on the Free plan. Upgrade to continue exploring new locations." : "You've used all your location searches for this month. Upgrade to continue exploring new locations."}</p>
          </div>
        </div>
      `;
    }
  
    let popupContent = `
      <div class="popup-content">
        ${locationNameHtml}
        <div class="date-range">
          ${selectedDateRange}
        </div>
        <div class="popup-content">
    `;

    const getHighlightClass = (section) => {
      if (isNation && mapStyle.activeTab !== 'trends') return '';
      
      const currentRange = mapStyle.range || getDefaultRange(mapStyle.activeTab)

      switch (currentRange) {
        case 'count':
          return section === 'total' ? 'highlighted-row' : '';
        case 'count_avg':
          return section === 'count_avg' ? 'highlighted-row' : '';
        case 'avg_diff':
          return section === 'avg_diff' ? 'highlighted-row' : '';
        case 'avg_diff_pct':
          return section === 'avg_diff_pct' ? 'highlighted-row' : '';
        case 'count_vs_avg':
          return section === 'count_vs_avg' ? 'highlighted-row' : '';
        case 'prev_count':
          return section === 'prev_count' ? 'highlighted-row' : '';
        case 'trend_diff':
          return section === 'trend_diff' ? 'highlighted-row' : '';
        case 'trend_diff_pct':
          return section === 'trend_diff_pct' ? 'highlighted-row' : '';
        case 'count_vs_prev':
          return section === 'count_vs_prev' ? 'highlighted-row' : '';
        case 'rate':
          return section === 'rate' ? 'highlighted-row' : '';
        case 'national_rate':
          return section === 'national_rate' ? 'highlighted-row' : '';
        case 'rate_diff':
          return section === 'rate_diff' ? 'highlighted-row' : '';
        case 'rate_diff_pct':
          return section === 'rate_diff_pct' ? 'highlighted-row' : '';
        case 'rate_vs_national_rate':
          return section === 'rate_vs_national_rate' ? 'highlighted-row' : '';
        case 'score':
          return section === 'score' ? 'highlighted-row' : '';
        case 'composite_score':
          return section === 'composite_score' ? 'highlighted-row' : '';
        case 'composite_score_rank':
          return section === 'composite_score_rank' ? 'highlighted-row' : '';
        case 'rank':
          return section === 'rank' ? 'highlighted-row' : '';
        case 'percentile':
          return section === 'percentile' ? 'highlighted-row' : '';
        default:
          return '';
      }
    };

    const hasCrimeData = [
      'all_crime_count', 'violent_crime_count', 'property_crime_count',
      'murder_count', 'rape_count', 'robbery_count', 'aggravated_assault_count',
      'burglary_count', 'larceny_theft_count', 'motor_vehicle_theft_count'
    ].some(field => 
      feature.properties[field] !== null && 
      feature.properties[field] !== undefined && 
      feature.properties[field] !== 'N/A'
    );

    if (mapStyle.activeTab === 'scores' && isNation) {
      popupContent += `
        <div class="no-data-message" style="text-align: center; padding: 20px;">
          Scores are not available<br> for the entire nation
        </div>
      `;
    } else if (!hasData || !hasCrimeData) {
      popupContent += `
        <div class="no-data-message" style="text-align: center; padding: 20px;">
          No Data Available
        </div>
      `;
    } else {
      switch (mapStyle.activeTab) {
        case 'reports':
        case 'totals':
          const avgField = isMetro 
            ? `${crimePrefix}_national_count_avg`
            : `${crimePrefix}_${compareMode === 'state' ? 'state' : 'national'}_count_avg`;
          const avgDiffField = isMetro
            ? `${crimePrefix}_national_count_avg_diff`
            : `${crimePrefix}_${compareMode === 'state' ? 'state' : 'national'}_count_avg_diff`;
          const avgDiffPctField = isMetro
            ? `${crimePrefix}_national_count_avg_diff_pct`
            : `${crimePrefix}_${compareMode === 'state' ? 'state' : 'national'}_count_avg_diff_pct`;
            
          const totalColor = getValueColor(feature.properties[avgDiffPctField]);
          
          popupContent += `
            <div class="data-row ${getHighlightClass('total')} ${getHighlightClass('count_vs_avg')}">
              <span class="label">Total ${crimeName}:</span>
              <strong>${formatNumber(feature.properties[`${crimePrefix}_count`])}</strong>
            </div>
          `;
          if (!isNation) {
            popupContent += `
              <div class="data-row ${getHighlightClass('count_avg')} ${getHighlightClass('count_vs_avg')}">
                <span class="label">${(isCounty || isCity || isMetro) ? 'US' : ''} ${locationType} Average:</span>
                <span>${formatNumber(feature.properties[avgField])}</span>
              </div>
              <div class="data-row ${getHighlightClass('avg_diff')} ${getHighlightClass('count_vs_avg')}">
                <span class="label">${(isCounty || isCity || isMetro) ? 'US' : ''} ${locationType} Avg Difference:</span>
                <span style="color: var(--${totalColor})">${formatDifference(feature.properties[avgDiffField])}</span>
              </div>
              <div class="data-row ${getHighlightClass('avg_diff_pct')}">
                <span class="label">% ${(isCounty || isCity || isMetro) ? 'US' : ''} ${locationType} Avg Difference:</span>
                <span style="color: var(--${totalColor})">${formatDifference(feature.properties[avgDiffPctField], true)}</span>
              </div>
              <div class="data-row ${getHighlightClass('rank')}">
                <span class="label">${(isCounty || isCity) ? `${compareMode === 'state' ? stateAbbr : 'US'}` : ''} ${locationType} Rank:</span>
                <span>${formatRank((isCounty || isCity) && compareMode === 'state' 
                  ? feature.properties[`${crimePrefix}_state_count_rank`] 
                  : (isCounty || isCity)
                    ? feature.properties[`${crimePrefix}_national_count_rank`] 
                    : feature.properties[`${crimePrefix}_count_rank`])}</span>
              </div>
              <div class="data-row ${getHighlightClass('percentile')}">
                <span class="label">${(isCounty || isCity) ? `${compareMode === 'state' ? stateAbbr : 'US'}` : ''} ${locationType} Percentile:</span>
                <span>${formatPercentile((isCounty || isCity) && compareMode === 'state' 
                  ? feature.properties[`${crimePrefix}_state_count_percentile`] 
                  : (isCounty || isCity)
                    ? feature.properties[`${crimePrefix}_national_count_percentile`] 
                    : feature.properties[`${crimePrefix}_count_percentile`])}</span>
              </div>
            `;
          }
          break;

        case 'trends':
          if (!hasPreviousData) {
            popupContent += `
              <div class="no-data-message" style="text-align: center; padding: 20px;">
                No Previous Data Available
              </div>`;
          } else {
            const trendValue = feature.properties[`${crimePrefix}_count_prev_diff_pct`];
            const trendColor = getValueColor(trendValue);
            popupContent += `
              <div class="data-row ${getHighlightClass('total')} ${getHighlightClass('count_vs_prev')}">
                <span class="label">Total ${crimeName}:</span>
                <span>${formatNumber(feature.properties[`${crimePrefix}_count`])}</span>
              </div>
              <div class="data-row ${getHighlightClass('prev_count')} ${getHighlightClass('count_vs_prev')}">
                <span class="label">Previous Total:</span>
                <span>${formatNumber(feature.properties[`${crimePrefix}_count_prev`])}</span>
              </div>
              <div class="data-row ${getHighlightClass('trend_diff')} ${getHighlightClass('count_vs_prev')}">
                <span class="label">Change from Previous Total:</span>
                <span style="color: var(--${trendColor})">${formatDifference(feature.properties[`${crimePrefix}_count_prev_diff`])}</span>
              </div>
              <div class="data-row ${getHighlightClass('trend_diff_pct')}">
                <span class="label">% Change from Previous Total:</span>
                <span style="color: var(--${trendColor}); font-weight: 600">${formatDifference(trendValue, true)}</span>
              </div>
            `;
            if (!isNation) {
              popupContent += `
                <div class="data-row ${getHighlightClass('rank')}">
                  <span class="label">${(isCounty || isCity) ? `${compareMode === 'state' ? stateAbbr : 'US'}` : ''} ${locationType} Rank:</span>
                  <span>${formatRank((isCounty || isCity) && compareMode === 'state' 
                    ? feature.properties[`${crimePrefix}_state_trend_rank`] 
                    : (isCounty || isCity)
                      ? feature.properties[`${crimePrefix}_national_trend_rank`] 
                      : feature.properties[`${crimePrefix}_trend_rank`])}</span>
                </div>
                <div class="data-row ${getHighlightClass('percentile')}">
                  <span class="label">${(isCounty || isCity) ? `${compareMode === 'state' ? stateAbbr : 'US'}` : ''} ${locationType} Percentile:</span>
                  <span>${formatPercentile((isCounty || isCity) && compareMode === 'state' 
                    ? feature.properties[`${crimePrefix}_state_trend_percentile`] 
                    : (isCounty || isCity)
                      ? feature.properties[`${crimePrefix}_national_trend_percentile`] 
                      : feature.properties[`${crimePrefix}_trend_percentile`])}</span>
                </div>
              `;
            }
          }
          break;

        case 'rates':
          const rateType = rateViewMode ? rateViewMode.rateType : 'county';
          const rateScope = rateViewMode ? rateViewMode.rateScope : 'state';
          
          const getComparisonLabel = () => {
            if (isMetro) {
              return rateType === 'metro' ? 'Metro' : 'National';
            }
            if (isCounty || isCity) {
              if (rateType === 'county') {
                return rateScope === 'state' ? `${stateAbbr} County` : 'US County';
              } else if (rateType === 'city') {
                return rateScope === 'state' ? `${stateAbbr} City` : 'US City';
              } else { 
                return rateScope === 'state' ? `${stateAbbr} State` : 'US National';
              }
            }
            return '';
          };
          
          let rankField, percentileField, avgRateField, rateDiffField, rateDiffPctField;
          
          if (isMetro && rateType === 'metro') {
            rankField = `${crimePrefix}_national_rate_rank`;
            percentileField = `${crimePrefix}_national_rate_percentile`;
            avgRateField = `${crimePrefix}_all_metros_rate`;
            rateDiffField = `${crimePrefix}_all_metros_rate_diff`;
            rateDiffPctField = `${crimePrefix}_all_metros_rate_diff_pct`;
          } else if (isMetro) {
            rankField = `${crimePrefix}_national_rate_rank`;
            percentileField = `${crimePrefix}_national_rate_percentile`;
            avgRateField = `${crimePrefix}_national_rate`;
            rateDiffField = `${crimePrefix}_national_rate_diff`;
            rateDiffPctField = `${crimePrefix}_national_rate_diff_pct`;
          } else if (rateType === 'county') {
            if (rateScope === 'state') {
              rankField = `${crimePrefix}_state_rate_rank`;
              percentileField = `${crimePrefix}_state_rate_percentile`;
              avgRateField = `${crimePrefix}_all_counties_state_rate`;
              rateDiffField = `${crimePrefix}_all_counties_state_rate_diff`;
              rateDiffPctField = `${crimePrefix}_all_counties_state_rate_diff_pct`;
            } else {
              rankField = `${crimePrefix}_national_rate_rank`;
              percentileField = `${crimePrefix}_national_rate_percentile`;
              avgRateField = `${crimePrefix}_all_counties_national_rate`;
              rateDiffField = `${crimePrefix}_all_counties_national_rate_diff`;
              rateDiffPctField = `${crimePrefix}_all_counties_national_rate_diff_pct`;
            }
          } else if (rateType === 'city') {
            if (rateScope === 'state') {
              rankField = `${crimePrefix}_state_rate_rank`;
              percentileField = `${crimePrefix}_state_rate_percentile`;
              avgRateField = `${crimePrefix}_all_cities_state_rate`;
              rateDiffField = `${crimePrefix}_all_cities_state_rate_diff`;
              rateDiffPctField = `${crimePrefix}_all_cities_state_rate_diff_pct`;
            } else {
              rankField = `${crimePrefix}_national_rate_rank`;
              percentileField = `${crimePrefix}_national_rate_percentile`;
              avgRateField = `${crimePrefix}_all_cities_national_rate`;
              rateDiffField = `${crimePrefix}_all_cities_national_rate_diff`;
              rateDiffPctField = `${crimePrefix}_all_cities_national_rate_diff_pct`;
            }
          } else {
            if (rateScope === 'state') {
              rankField = `${crimePrefix}_state_rate_rank`;
              percentileField = `${crimePrefix}_state_rate_percentile`;
              avgRateField = `${crimePrefix}_state_rate`;
              rateDiffField = `${crimePrefix}_state_rate_diff`;
              rateDiffPctField = `${crimePrefix}_state_rate_diff_pct`;
            } else {
              rankField = `${crimePrefix}_national_rate_rank`;
              percentileField = `${crimePrefix}_national_rate_percentile`;
              avgRateField = `${crimePrefix}_national_rate`;
              rateDiffField = `${crimePrefix}_national_rate_diff`;
              rateDiffPctField = `${crimePrefix}_national_rate_diff_pct`;
            }
          }
          
          const rate = feature.properties[`${crimePrefix}_rate`];
          const compareRateField = feature.properties[avgRateField] || feature.properties[`${crimePrefix}_national_rate`];
          const rateColor = getRateColor(rate, compareRateField);
          const rateDiff = feature.properties[rateDiffField] || feature.properties[`${crimePrefix}_national_rate_diff`];
          const ratePercentDiff = feature.properties[rateDiffPctField] || feature.properties[`${crimePrefix}_national_rate_diff_pct`];
          
          popupContent += `
            <div class="data-row ${getHighlightClass('rate')} ${getHighlightClass('rate_vs_national_rate')}">
              <span class="label">${crimeName} Rate:</span>
              <span style="color: var(--${rateColor})">${formatNumber(rate)}</span>
            </div>
          `;
          
          if (!isNation) {
            popupContent += `
              <div class="data-row ${getHighlightClass('national_rate')} ${getHighlightClass('rate_vs_national_rate')}">
                <span class="label">${(isCounty || isCity || isMetro) ? getComparisonLabel() : 'National'} Rate:</span>
                <span>${formatNumber(compareRateField)}</span>
              </div>
              <div class="data-row ${getHighlightClass('rate_diff')} ${getHighlightClass('rate_vs_national_rate')}">
                <span class="label">${(isCounty || isCity || isMetro) ? getComparisonLabel() : 'National'} Rate Difference:</span>
                <span style="color: var(--${getRateDiffColor(rateDiff)}); font-weight: 600">${formatDifference(rateDiff, false, true)}</span>
              </div>
              <div class="data-row ${getHighlightClass('rate_diff_pct')}">
                <span class="label">% ${(isCounty || isCity || isMetro) ? getComparisonLabel() : 'National'} Rate Difference:</span>
                <span style="color: var(--${getValueColor(ratePercentDiff)})">${formatDifference(ratePercentDiff, true)}</span>
              </div>
              <div class="data-row ${getHighlightClass('rank')}">
                <span class="label">${(isCounty || isCity) ? getComparisonLabel() : locationType} Rank:</span>
                <span>${formatRank(feature.properties[rankField] || feature.properties[`${crimePrefix}_rate_rank`])}</span>
              </div>
              <div class="data-row ${getHighlightClass('percentile')}">
                <span class="label">${(isCounty || isCity) ? getComparisonLabel() : locationType} Percentile:</span>
                <span>${formatPercentile(feature.properties[percentileField] || feature.properties[`${crimePrefix}_rate_percentile`])}</span>
              </div>
            </div>
          `;
          }
          break;

        case 'scores':
          const getScoreFieldKey = (prefix) => {
            if (!isCounty && !isCity) return `${prefix}_score`;
            
            return compareMode === 'state' 
              ? `${prefix}_state_score` 
              : `${prefix}_national_score`;
          };
          
          const getScoreRankFieldKey = (prefix) => {
            if (!isCounty && !isCity) return `${prefix}_score_rank`;
            
            return compareMode === 'state' 
              ? `${prefix}_state_score_rank` 
              : `${prefix}_national_score_rank`;
          };
          
          const getCompositeFieldKey = (baseField) => {
            if (!isCounty && !isCity) return baseField;
            
            return compareMode === 'state'
              ? `state_${baseField}`
              : `national_${baseField}`;
          };
          
          const scoreValue = feature.properties[getScoreFieldKey(crimePrefix)];
          const compositeScore = feature.properties[getCompositeFieldKey('composite_score')];
          const compositeRank = feature.properties[getCompositeFieldKey('composite_score_rank')];
          const scoreRank = feature.properties[getScoreRankFieldKey(crimePrefix)];
        
          popupContent += `
            <div class="data-row ${getHighlightClass('composite_score')}">
              <span class="label">${(isCounty || isCity) ? `${compareMode === 'state' ? stateAbbr : 'US'}` : ''} KRIME Score™ (Composite):</span>
              <span style="color: ${getScoreColor(compositeScore)}; font-weight: 600">
                ${formatNumber(compositeScore, true)}
              </span>
            </div>
            <div class="data-row ${getHighlightClass('composite_score_rank')}">
              <span class="label">${(isCounty || isCity) ? `${compareMode === 'state' ? stateAbbr : 'US'}` : ''} ${locationType} Rank (Composite):</span>
              <span>${formatRank(compositeRank)}</span>
            </div>
            <div class="data-row ${getHighlightClass('score')}">
              <span class="label">${(isCounty || isCity) ? `${compareMode === 'state' ? stateAbbr : 'US'}` : ''} ${mapStyle.crime === 'all_crime' ? 'All ' : ''}${crimeName} Score (Non-Weighted):</span>
              <span style="color: ${getScoreColor(scoreValue)}; font-weight: 600">
                <em>${formatNumber(scoreValue, true)}</em>
              </span>
            </div>
            <div class="data-row ${getHighlightClass('rank')}">
              <span class="label">${(isCounty || isCity) ? `${compareMode === 'state' ? stateAbbr : 'US'}` : ''} ${locationType} Rank (Non-Weighted):</span>
              <span><em>${formatRank(scoreRank)}</em></span>
            </div>
          </div>
        `;
        break;
        
        default:
          return '';
      }
    }

    popupContent += `</div></div>`;
    return popupContent;
  }, [
    isCounty,
    isCity,
    isAllCountiesNationwide,
    isAllCitiesNationwide,
    isNation,
    isMetro,
    getClickedCity,
    mapStyle.activeTab, 
    mapStyle.range, 
    mapStyle.crime,
    crimeName,
    crimePrefix,
    locationType,
    selectedLocation, 
    selectedDateRange, 
    compareMode, 
    hasData, 
    hasPreviousData,
    rateViewMode,
    mapSearchGateRef,
    userPlan,
  ]);

  const generateStatePopupContent = useCallback((feature) => {
    if (!feature?.properties?.name) return '';

    const isDC = isCounty && feature.properties.name === 'District of Columbia';

    if (isDC) {
      return `
        <div class="popup-content">
          <div class="location-name" style="text-align: center;">
            ${feature.properties.name}
          </div>
          <div class="state-click-hint" style="font-size: 12px;">
            No County Data Available
          </div>
        </div>
      `;
    }
    
    return `
      <div class="popup-content">
        <div class="location-name" style="text-align: center;">
          ${feature.properties.name}
        </div>
        <div class="state-click-hint" style="font-size: 12px; text-align: center;">
          Click to View All ${isCounty ? 'Counties' : 'Cities'}
        </div>
      </div>
    `;
  }, [isCounty]);

  const updateHoverPopup = useCallback((feature, latlng, clickEvent = null) => {
    if (!map || !feature || disableInteractions) return;
    
    if (!hoverPopupRef.current) {
      const popupContainer = document.createElement('div');
      popupContainer.className = 'leaflet-popup custom-popup hover-popup';
      popupContainer.style.position = 'fixed';
      popupContainer.style.zIndex = '1001';
      popupContainer.style.pointerEvents = 'none';
      popupContainer.style.transform = 'translate(-50%, -100%)';
      popupContainer.innerHTML = `
        <div class="leaflet-popup-content-wrapper">
          <div class="leaflet-popup-content"></div>
        </div>
        <div class="leaflet-popup-tip-container">
          <div class="leaflet-popup-tip"></div>
        </div>
      `;
      document.body.appendChild(popupContainer);
      hoverPopupRef.current = popupContainer;
      hoverPopupContentRef.current = popupContainer.querySelector('.leaflet-popup-content');
    }
    
    const content = generatePopupContent(feature, clickEvent);
    hoverPopupContentRef.current.innerHTML = content;
    
    const mapContainer = map.getContainer();
    const mapRect = mapContainer.getBoundingClientRect();
    const point = map.latLngToContainerPoint(latlng);
    
    hoverPopupRef.current.style.left = `${mapRect.left + point.x}px`;
    hoverPopupRef.current.style.top = `${mapRect.top + point.y - 13}px`;
    hoverPopupRef.current.style.display = 'block';
  }, [map, generatePopupContent, disableInteractions]);
  
  const hideHoverPopup = useCallback(() => {
    if (hoverPopupRef.current) {
      hoverPopupRef.current.style.display = 'none';
    }
    setHoveredLocation(null);
  }, [setHoveredLocation]);  

  useEffect(() => {
    return () => {
      if (hoverPopupRef.current && document.body.contains(hoverPopupRef.current)) {
        document.body.removeChild(hoverPopupRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!map) return;
    
    const mapContainer = map.getContainer();
    
    const handleMapMove = () => { hideHoverPopup(); };
    const handleMapMouseOut = () => { hideHoverPopup(); };
    
    map.on('movestart', handleMapMove);
    map.on('zoomstart', handleMapMove);
    map.on('click', hideHoverPopup);
    
    mapContainer.addEventListener('mouseout', handleMapMouseOut);
    
    return () => {
      map.off('movestart', handleMapMove);
      map.off('zoomstart', handleMapMove);
      map.off('click', hideHoverPopup);
      
      mapContainer.removeEventListener('mouseout', handleMapMouseOut);
    };
  }, [map, hideHoverPopup]);

  useEffect(() => {
    if (!layerRef.current || !map) return;
    
    if (clickedLayerRef.current) return;
    
    const hoverBorderOverlays = hoverBorderOverlaysRef.current;
    
    hoverBorderOverlays.forEach((borderLayer, layer) => {
      if (layer._isTableHover) {
        map.removeLayer(borderLayer);
        hoverBorderOverlays.delete(layer);
        layer._isTableHover = false;
      }
    });
    
    if (!hoveredLocation) return;
    
    layerRef.current.eachLayer((layer) => {
      const feature = layer.feature;
      if (!feature?.properties) return;
      
      if (selectedLayerRef.current === layer) return;
      
      let layerLocationName = '';
      if (feature.properties.county && feature.properties.state) {
        layerLocationName = (feature.properties.county === 'Carson City' && feature.properties.state === 'Nevada') ?
          `${feature.properties.county}, ${feature.properties.state}` :
          (feature.properties.state === 'Louisiana' ?
            `${feature.properties.county} Parish, ${feature.properties.state}` :
          feature.properties.state === 'Alaska' ?
            `${feature.properties.county} Borough, ${feature.properties.state}` :
            `${feature.properties.county} County, ${feature.properties.state}`);
      } else if (feature.properties.city && feature.properties.state) {
        layerLocationName = `${feature.properties.city}, ${feature.properties.state}`;
      } else {
        layerLocationName = feature.properties.name;
      }
      
      if (layerLocationName === hoveredLocation) {
        if (hoverBorderOverlays.has(layer) && !layer._isTableHover) return;
        
        const borderLayer = L.geoJSON(feature, {
          interactive: false,
          style: {
            weight: 5,
            color: 'var(--navy)',
            fillOpacity: 0,
            opacity: 0.8
          }
        }).addTo(map);
        
        layer._isTableHover = true;
        hoverBorderOverlays.set(layer, borderLayer);
      }
    });
  }, [hoveredLocation, map]);

  useEffect(() => {
    if (allCountiesCitiesPopupLayerRef.current && layerRef.current) {
      layerRef.current.eachLayer((layer) => {
        layer.closePopup();
      });
      allCountiesCitiesPopupLayerRef.current = null;
    }
    
    cancelPopupOpenRef.current = false;
    if (clickedLayerRef.current) {
      clickedLayerRef.current.closePopup();
      clickedLayerRef.current = null;
    }
    map?.closePopup();
    onPopupOpen(false);
  }, [selectedLocation?.info?.name, selectedLocation?.info?.type, onPopupOpen, map]);

  useEffect(() => {
    if (!disableInteractions) return;

    hideHoverPopup();
    cancelPopupOpenRef.current = true;

    if (allCountiesCitiesPopupLayerRef.current) {
      allCountiesCitiesPopupLayerRef.current.closePopup?.();
      allCountiesCitiesPopupLayerRef.current = null;
    }

    if (clickedLayerRef.current) {
      clickedLayerRef.current.closePopup();
      clickedLayerRef.current = null;
    }

    map?.closePopup();
    onPopupOpen(false);
  }, [disableInteractions, hideHoverPopup, map, onPopupOpen]);

  useEffect(() => {
    setCurrentGeometryCity(null);
    setCurrentPopupFeature(null);
  }, [selectedLocation, mapStyle.activeTab, mapStyle.range, mapStyle.crime]);

  useEffect(() => {
    if (!map || !layerRef.current) return;
    
    const updateSelectedBorder = () => {
      if (!layerRef.current) return;

      if (selectedBorderOverlayRef.current) {
        map.removeLayer(selectedBorderOverlayRef.current);
        selectedBorderOverlayRef.current = null;
        selectedLayerRef.current = null;
      }
      
      layerRef.current.eachLayer((layer) => {
        const feature = layer.feature;
        if (!feature?.properties) return;
        
        const isThisSelected = (isCounty || isCity) 
          ? (isCounty && (
              selectedLocation?.info?.name === `${feature.properties.county} County, ${feature.properties.state}` ||
              selectedLocation?.info?.name === `${feature.properties.county} Parish, ${feature.properties.state}` ||
              selectedLocation?.info?.name === `${feature.properties.county} Borough, ${feature.properties.state}` ||
              selectedLocation?.info?.name === `${feature.properties.county}, ${feature.properties.state}`
            )) ||
            (isCity && selectedLocation?.info?.name === `${feature.properties.city}, ${feature.properties.state}`)
          : selectedLocation?.info?.name === feature.properties.name;
        
        if (isThisSelected) {
          selectedLayerRef.current = layer;
          
          const hoverBorderOverlays = hoverBorderOverlaysRef.current;
          if (hoverBorderOverlays.has(layer)) {
            map.removeLayer(hoverBorderOverlays.get(layer));
            hoverBorderOverlays.delete(layer);
          }
          
          selectedBorderOverlayRef.current = L.geoJSON(feature, {
            interactive: false,
            style: {
              weight: !isNation ? 5 : 2,
              color: 'var(--navy)',
              fillOpacity: 0,
              opacity: 0.8
            },
            pane: 'markerPane' 
          }).addTo(map);
          return;
        }
      });
    };
    
    updateSelectedBorder();
    
    const handleViewReset = () => {
      if (!map._animatingZoom && !map._panAnim?._inProgress) {
        updateSelectedBorder();
      }
    };
    
    map.on('zoomend', handleViewReset);
    map.on('moveend', handleViewReset);
    map.on('viewreset', handleViewReset);
    
    return () => {
      map.off('zoomend', handleViewReset);
      map.off('moveend', handleViewReset);
      map.off('viewreset', handleViewReset);
      if (selectedBorderOverlayRef.current) {
        map.removeLayer(selectedBorderOverlayRef.current);
      }
    };
  }, [map, selectedLocation?.info?.name, selectedLocation?.info?.type, isCounty, isCity, isNation]);

  useEffect(() => {
    if (!layerRef.current) return;

    const hoverBorderOverlays = hoverBorderOverlaysRef.current;

    layerRef.current.eachLayer((layer) => {
      layer.off('mouseover');
      layer.off('mousemove');
      layer.off('mouseout');
      layer.off('click');
      layer.off('popupopen');
      layer.off('popupclose');
    });
    
    layerRef.current.eachLayer((layer) => {
      const feature = layer.feature;
      if (!feature?.properties) return;
  
      if (isAllCountiesNationwide || isAllCitiesNationwide) {
        const popupContent = generatePopupContent(feature);
        
        layer.bindPopup(popupContent, {
          className: 'custom-popup all-counties-popup',
          interactive: true,
          autoPan: false
        });
        
        layer.on({
          mouseover: (e) => {
            if (disableInteractions || isAnyMenuOpen || clickedLayerRef.current || preventHoverUntilMouseMove.current) return;
            
            if (!isMobile && !isTouchDevice && !isTransitioning.current) {  
              const feature = e.target.feature;
    
              if (selectedLayerRef.current === e.target) {
                return;
              }
    
              if (hoverBorderOverlays.has(e.target)) {
                map.removeLayer(hoverBorderOverlays.get(e.target));
                hoverBorderOverlays.delete(e.target);
              }
              
              const borderLayer = L.geoJSON(feature, {
                interactive: false,
                style: {
                  weight: 5,
                  color: 'var(--navy)',
                  fillOpacity: 0,
                  opacity: 0.8
                }
              }).addTo(map);
              
              hoverBorderOverlays.set(e.target, borderLayer);

              const featureProperties = e.target.feature.properties;
              
              if (selectedLocation.info?.type === 'county') {
                setHoveredLocation(`${featureProperties.county}, ${featureProperties.state}`);
              } else if (selectedLocation.info?.type === 'city') {
                setHoveredLocation(`${featureProperties.city}, ${featureProperties.state}`);
              } else {
                setHoveredLocation(featureProperties.name);
              }
              
              if (!clickedLayerRef.current) {
                const mousePoint = e.originalEvent;
                const popupLatLng = e.target._map.mouseEventToLatLng(mousePoint);
                updateHoverPopup(e.target.feature, popupLatLng);
              }
            } else {
              hideHoverPopup();
            }
          },
          mousemove: (e) => {
            if (disableInteractions || isAnyMenuOpen || preventHoverUntilMouseMove.current) return;

            if (!isMobile && !isTouchDevice && !isTransitioning.current) {  
              const mousePoint = e.originalEvent;
              const popupLatLng = e.target._map.mouseEventToLatLng(mousePoint);
              updateHoverPopup(e.target.feature, popupLatLng);
            } else {
              hideHoverPopup();
            }
          },
          mouseout: (e) => {
            if (!isMobile) {
              const borderLayer = hoverBorderOverlays.get(e.target);
              if (borderLayer) {
                map.removeLayer(borderLayer);
                hoverBorderOverlays.delete(e.target);
              }

              setHoveredLocation(null);
              if (!clickedLayerRef.current) {
                hideHoverPopup();
              }
            }
          },
          click: (e) => {
            if (disableInteractions) return;
            e.originalEvent.stopPropagation();
            hideHoverPopup();
            preventHoverUntilMouseMove.current = true;
            
            const stateName = e.target.feature.properties.name;
            const stateAbbr = getStateAbbreviation(stateName);

            if (allCountiesCitiesPopupLayerRef.current) {
              allCountiesCitiesPopupLayerRef.current.closePopup();
              allCountiesCitiesPopupLayerRef.current = null;
            }

            if (isAllCountiesNationwide && stateName === 'District of Columbia') return;

            allCountiesCitiesPopupLayerRef.current = layer;
            
            if (isAllCountiesNationwide) {
              onLocationSelect({
                name: `All ${stateAbbr} Counties`,
                type: 'county',
                info: {
                  name: `All ${stateAbbr} Counties`,
                  type: 'county',
                  state: stateName,
                  stateAbbr: stateAbbr
                }
              });
            } else if (isAllCitiesNationwide) {
              onLocationSelect({
                name: `All ${stateAbbr} Cities`,
                type: 'city',
                info: {
                  name: `All ${stateAbbr} Cities`,
                  type: 'city',
                  state: stateName,
                  stateAbbr: stateAbbr
                }
              });
            }
            
            if (clickedLayerRef.current) {
              clickedLayerRef.current.closePopup();
              clickedLayerRef.current = null;
              onPopupOpen(false);
            }            
          }
        });
        
        return;
      }
  
      const popupContent = generatePopupContent(feature);
      
      layer.bindPopup(popupContent, {
        className: 'custom-popup',
        interactive: true,
        autoPan: false
      });
  
      layer.on({
        mouseover: (e) => {
          if (disableInteractions || isAnyMenuOpen || clickedLayerRef.current || preventHoverUntilMouseMove.current) return;
          
          if (!isMobile && !isTouchDevice && !isTransitioning.current) { 
            const feature = e.target.feature;
    
            if (selectedLayerRef.current === e.target) {
              return;
            }
          
            if (hoverBorderOverlays.has(e.target)) {
              map.removeLayer(hoverBorderOverlays.get(e.target));
              hoverBorderOverlays.delete(e.target);
            }
            
            const borderLayer = L.geoJSON(feature, {
              interactive: false,
              style: {
                weight: 5,
                color: 'var(--navy)',
                fillOpacity: 0,
                opacity: 0.8
              }
            }).addTo(map);
            
            hoverBorderOverlays.set(e.target, borderLayer);

            const featureProperties = e.target.feature.properties;
            
            if (selectedLocation.info?.type === 'county') {
              setHoveredLocation(`${featureProperties.county}, ${featureProperties.state}`);
            } else if (selectedLocation.info?.type === 'city') {
              setHoveredLocation(`${featureProperties.city}, ${featureProperties.state}`);
              
              setHoveredSharedCity({
                city: featureProperties.city,
                state: featureProperties.state
              });
            } else {
              setHoveredLocation(featureProperties.name);
            }
            
            if (!clickedLayerRef.current) {
              const mousePoint = e.originalEvent;
              const popupLatLng = e.target._map.mouseEventToLatLng(mousePoint);
              updateHoverPopup(e.target.feature, popupLatLng, {
                latlng: popupLatLng,
                originalEvent: e.originalEvent
              });
            }
          } else {
            hideHoverPopup();
          }
        },
        mousemove: (e) => {
          if (disableInteractions || isAnyMenuOpen || preventHoverUntilMouseMove.current) return;

          if (!isMobile && !isTouchDevice && !isTransitioning.current) { 
            const mousePoint = e.originalEvent;
            const popupLatLng = e.target._map.mouseEventToLatLng(mousePoint);
            const moveEvent = {
              latlng: popupLatLng,
              originalEvent: e.originalEvent
            };
            
            const feature = e.target.feature;
            if (feature.properties.constituent_cities && feature.properties.constituent_cities.length > 0) {
              const newGeometryCity = getClickedCity(moveEvent, feature);
              
              if (newGeometryCity) {
                const newHoverName = `${feature.properties.city} (${newGeometryCity}), ${feature.properties.state}`;
                setHoveredLocation(newHoverName);
              }
              
              if (!clickedLayerRef.current) {
                updateHoverPopup(feature, popupLatLng, moveEvent);
              }
            } else {
              if (!clickedLayerRef.current) {
                updateHoverPopup(e.target.feature, popupLatLng, moveEvent);
              }
            }
          } else {
            hideHoverPopup();
          }
        },
        mouseout: (e) => {
          if (!isMobile) {
            const borderLayer = hoverBorderOverlays.get(e.target);
            if (borderLayer) {
              map.removeLayer(borderLayer);
              hoverBorderOverlays.delete(e.target);
            }

            setHoveredLocation(null);
            setCurrentGeometryCity(null);
            setHoveredSharedCity(null);
            
            if (!clickedLayerRef.current) {
              hideHoverPopup();
            }
          }
        },
        click: (e) => {
          if (disableInteractions) return;
          e.originalEvent.stopPropagation();
          e.originalEvent._stopped = true;
          hideHoverPopup();
          preventHoverUntilMouseMove.current = true;
          cancelPopupOpenRef.current = false;
          clickInitiatedRef.current = true;
        
          const featureProperties = e.target.feature.properties;
          const mousePoint = e.originalEvent;
          const clickLatLng = e.target._map.mouseEventToLatLng(mousePoint);
          const clickEvent = {
            latlng: clickLatLng,
            originalEvent: e.originalEvent
          };
          
          setCurrentPopupFeature(e.target.feature);
          const clickedCity = getClickedCity(clickEvent, e.target.feature);
          setCurrentGeometryCity(clickedCity);

          const isAlaskaOrHawaii = featureProperties.name === 'Alaska' || featureProperties.name === 'Hawaii';
          const currentZoom = map.getZoom();
        
          let needsTransition = false;
          if (isAlaskaOrHawaii) {
            const targetZoom = isMapMobile ? 2 : 3;
            needsTransition = currentZoom !== targetZoom;
          } else if (featureProperties.metro || (featureProperties.name && !featureProperties.county && !featureProperties.city && !featureProperties.state)) {
            needsTransition = true;
          } else if (!featureProperties.county && !featureProperties.city) {
            const targetZoom = isMapMobile ? 3 : 4;
            needsTransition = currentZoom !== targetZoom;
          } else if (featureProperties.city && featureProperties.state) {
            needsTransition = true;
          }
        
          if (clickedLayerRef.current) {
            clickedLayerRef.current.closePopup();
            clickedLayerRef.current = null;
          }
        
          if (needsTransition) {
            isTransitioning.current = true;
            hideHoverPopup();
            layer.closePopup();
          }

          const hasCrimeData = [
            'all_crime_count', 'violent_crime_count', 'property_crime_count',
            'murder_count', 'rape_count', 'robbery_count', 'aggravated_assault_count',
            'burglary_count', 'larceny_theft_count', 'motor_vehicle_theft_count'
          ].some(field => 
            featureProperties[field] !== null && 
            featureProperties[field] !== undefined && 
            featureProperties[field] !== 'N/A'
          );
        
          if (featureProperties.county && featureProperties.state) {
            if (hasCrimeData) {
              onLocationSelect({
                name: (featureProperties.county === 'Carson City' && featureProperties.state === 'Nevada') ?
                  `${featureProperties.county}, ${featureProperties.state}` :
                  (featureProperties.state === 'Louisiana' ?
                    `${featureProperties.county} Parish, ${featureProperties.state}` :
                  featureProperties.state === 'Alaska' ?
                    `${featureProperties.county} Borough, ${featureProperties.state}` :
                    `${featureProperties.county} County, ${featureProperties.state}`),
                type: 'county',
                info: {
                  name: (featureProperties.county === 'Carson City' && featureProperties.state === 'Nevada') ?
                    `${featureProperties.county}, ${featureProperties.state}` :
                    (featureProperties.state === 'Louisiana' ?
                      `${featureProperties.county} Parish, ${featureProperties.state}` :
                    featureProperties.state === 'Alaska' ?
                      `${featureProperties.county} Borough, ${featureProperties.state}` :
                      `${featureProperties.county} County, ${featureProperties.state}`),
                  type: 'county',
                  state: featureProperties.state,
                },
              });
            }
          } else if (featureProperties.city && featureProperties.state) {
            if (hasCrimeData) {
              onLocationSelect({
                name: `${featureProperties.city}, ${featureProperties.state}`,
                type: 'city',
                info: {
                  name: `${featureProperties.city}, ${featureProperties.state}`,
                  type: 'city',
                  state: featureProperties.state,
                  clickInitiated: true,
                  skipResolutionCheck: true
                },
              });
            }
          } else {
            onLocationSelect({
              name: featureProperties.name,
              type: selectedLocation.info.type,
              info: {
                name: featureProperties.name,
                type: selectedLocation.info.type,
              },
            });
          }

          setTimeout(() => {
            clickInitiatedRef.current = false;
          }, 100);
            
          const openPopup = () => {
            if (cancelPopupOpenRef.current || searchBlockedRef?.current) {
              cancelPopupOpenRef.current = false;
              if (searchBlockedRef?.current) searchBlockedRef.current = false;
              if (needsTransition) {
                isTransitioning.current = false;
              }
              return;
            }

            if (programmaticZoomRef.current) {
              map.once('moveend', () => openPopup());
              return;
            }

            const popupContent = generatePopupContent(e.target.feature, clickEvent);
            
            const popup = layer.getPopup();
            if (popup) {
              popup.setContent(popupContent);
              popup.options.autoPan = true;
              popup.options.autoPanPadding = getPopupPadding();
              popup.setLatLng(clickLatLng);
            }
        
            clickedLayerRef.current = layer;
            recenterOnPopupClose.current = true;
            layer.openPopup(clickLatLng);
            onPopupOpen(true);
            if (needsTransition) {
              isTransitioning.current = false;
            }
          };
        
          if (needsTransition) {
            setTimeout(openPopup, 400);
          } else {
            requestAnimationFrame(openPopup);
          }
        },
        popupopen: (e) => {
          const popup = e.popup;
          const closeButton = popup._closeButton;
  
          if (closeButton) {
            closeButton.onclick = null;
            
            closeButton.addEventListener('mousedown', () => {
              if (isProcessingClose.current) return;
              isProcessingClose.current = true;
                            
              if (popup) {
                popup.options.autoPan = false;
                popup.options.autoPanPadding = null;
              }
              
              clickedLayerRef.current = null;
              onPopupOpen(false);
              layer.closePopup();
              
              setTimeout(() => {
                if (map) {
                  map.fire('click');
                  isProcessingClose.current = false;
                }
              }, 100);
            });
          }
        },
        popupclose: (e) => {
          if (clickedLayerRef.current === layer) {
            const popup = layer.getPopup();
            if (popup) {
              popup.options.autoPan = false;
              popup.options.autoPanPadding = null;
            }
            clickedLayerRef.current = null;
            setCurrentPopupFeature(null);
            setCurrentGeometryCity(null);
            onPopupOpen(false);
          }
        }
      });
    });

    return () => {
      hoverBorderOverlays.forEach(layer => {
        if (map.hasLayer(layer)) {
          map.removeLayer(layer);
        }
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapStyle.activeTab, mapStyle.range, mapStyle.crime, isMobile, compareMode, rateViewMode, isAnyMenuOpen]);

  const getStyleFunction = useCallback((feature) => {
    if (!locationTypeData?.data?.locations || !feature.properties) return {};
  
    if (isAllCountiesNationwide || isAllCitiesNationwide) {
      const isHovered = hoveredLocation === feature.properties.name;
      
      return {
        fillColor: 'var(--silver)',
        weight: isHovered ? 5 : 2,
        opacity: 0.6,
        color: 'var(--navy)',
        dashArray: '0',
        fillOpacity: 0.6
      };
    }
    
    const isSelected = (isCounty || isCity) 
      ? (isCounty && (
          selectedLocation?.info?.name === `${feature.properties.county} County, ${feature.properties.state}` ||
          selectedLocation?.info?.name === `${feature.properties.county} Parish, ${feature.properties.state}` ||
          selectedLocation?.info?.name === `${feature.properties.county} Borough, ${feature.properties.state}` ||
          selectedLocation?.info?.name === `${feature.properties.county}, ${feature.properties.state}`
        )) ||
        (isCity && selectedLocation?.info?.name === `${feature.properties.city}, ${feature.properties.state}`)
      : selectedLocation?.info?.name === feature.properties.name;
    
    const isHovered = (isCounty || isCity) 
      ? (isCounty && (
          hoveredLocation === `${feature.properties.county}, ${feature.properties.state}` ||
          hoveredLocation === `${feature.properties.county} County, ${feature.properties.state}` ||
          hoveredLocation === `${feature.properties.county} Parish, ${feature.properties.state}` ||
          hoveredLocation === `${feature.properties.county} Borough, ${feature.properties.state}`
        )) ||
        (isCity && hoveredLocation === `${feature.properties.city}, ${feature.properties.state}`)
      : hoveredLocation === feature.properties.name;

    const isSharedCityHovered = hoveredSharedCity && 
      feature.properties.city === hoveredSharedCity.city && 
      feature.properties.state === hoveredSharedCity.state;
  
    if ((mapStyle.activeTab === 'trends' && !hasPreviousData) || !hasData) {
      const isStateView = (isCounty || isCity) && !(isAllCountiesNationwide || isAllCitiesNationwide);

      return {
        fillColor: 'var(--silver)',
        weight: !isNation && (isSelected || isHovered) ? 5 : 2,
        opacity: 0.6,
        color: 'var(--navy)',
        dashArray: '0',
        fillOpacity: isStateView ? 0 : 0.6
      };
    }
  
    if (isNation && mapStyle.activeTab === 'trends') {
      if (mapStyle.range === 'count' || mapStyle.range === 'prev_count') {
        return {
          fillColor: 'var(--silver)',
          weight: 2,
          opacity: 0.6,
          color: 'var(--navy)',
          dashArray: '0',
          fillOpacity: 0.6
        };
      }
      
      const range = mapStyle.range || getDefaultRange(mapStyle.activeTab);
      const trendValue = getValueForRange(feature, range, crimePrefix, mapStyle.activeTab, compareMode, rateViewMode);
      const { min, max } = getMinMaxValues(
        locationTypeData.data.locations, 
        range, 
        crimePrefix,
        mapStyle.activeTab,
        compareMode,
        rateViewMode
      );
  
      return {
        fillColor: getMapColor(trendValue, min, max, 'trends', null, range),
        weight: 2,
        opacity: 0.6,
        color: 'var(--navy)',
        fillOpacity: 0.6
      };
    } else if (isNation) {
      return {
        fillColor: 'var(--silver)',
        weight: 2,
        opacity: 0.6,
        color: 'var(--navy)',
        fillOpacity: 0.6
      };
    }
  
    const range = mapStyle.range || getDefaultRange(mapStyle.activeTab);
    const value = getValueForRange(feature, range, crimePrefix, mapStyle.activeTab, compareMode, rateViewMode, locTypeString);
    const { min, max } = getMinMaxValues(
      locationTypeData.data.locations, 
      range, 
      crimePrefix,
      mapStyle.activeTab,
      compareMode,
      rateViewMode,
      locTypeString
    );
  
    const effectiveTab = getEffectiveTab(range);
  
    let comparisonRate = null;
    if (range === 'rate' && mapStyle.activeTab === 'rates') {
      if ((isCounty || isCity || isMetro) && rateViewMode) {
        const rateType = rateViewMode.rateType || 'county';
        const rateScope = rateViewMode.rateScope || 'state';
        
        if (isMetro && rateType === 'metro') {
          comparisonRate = feature.properties[`${crimePrefix}_all_metros_rate`];
        } else if (isMetro) {
          comparisonRate = feature.properties[`${crimePrefix}_national_rate`];
        } else if (rateType === 'county') {
          comparisonRate = feature.properties[rateScope === 'state' 
            ? `${crimePrefix}_all_counties_state_rate` 
            : `${crimePrefix}_all_counties_national_rate`];
        } else if (rateType === 'city') {
          comparisonRate = feature.properties[rateScope === 'state'
            ? `${crimePrefix}_all_cities_state_rate`
            : `${crimePrefix}_all_cities_national_rate`];
        } else {
          comparisonRate = feature.properties[rateScope === 'state'
            ? `${crimePrefix}_state_rate`
            : `${crimePrefix}_national_rate`];
        }
      } else {
        comparisonRate = feature.properties[`${crimePrefix}_national_rate`];
      }
    }
  
    const useMin = (range === 'rank' || range === 'composite_score_rank') ? max : min;
    const useMax = (range === 'rank' || range === 'composite_score_rank') ? min : max;
  
    return {
      fillColor: getMapColor(value, useMin, useMax, effectiveTab === 'rank' ? 'reports' : effectiveTab, comparisonRate, range),
      weight: isSelected || isHovered || isSharedCityHovered ? 5 : 2,
      opacity: isSelected || isHovered || isSharedCityHovered ? 0 : isCity ? 0.2 : 0.8,
      color: 'var(--navy)',
      dashArray: '0',
      fillOpacity: 0.8
    };
  }, [
    locationTypeData?.data?.locations, 
    isCounty,
    isCity,
    isAllCountiesNationwide,
    isAllCitiesNationwide,
    isNation,
    isMetro,
    crimePrefix,
    mapStyle, 
    hoveredLocation,
    hoveredSharedCity,
    selectedLocation,
    hasPreviousData, 
    hasData, 
    compareMode, 
    rateViewMode,
    locTypeString
  ]);
  
  return (
    <>
      {stateFeatures && (isCounty || isCity || isMetro) && (
        <GeoJSON
          key={`state-boundaries-${locationTypeData.type}-${selectedLocation?.info?.state || locationTypeData.currentState || 'none'}`}
          ref={stateBoundariesLayerRef}
          data={{
            type: "FeatureCollection",
            features: stateFeatures
          }}
          style={(feature) => {
            let currentStateName = null;

            if (isMetro) {
              return {
                weight: 2,
                color: 'var(--navy)',
                fillOpacity: 0,
                opacity: 0.2
              };
            }
            
            if (isAllCountiesInState || isAllCitiesInState) {
              let statePart = selectedLocation.info.name
                .replace(/^All /, '')
                .replace(/ Counties$/, '')
                .replace(/ Cities$/, '');
              currentStateName = statePart.length === 2 ? getFullStateName(statePart) : statePart;
            } else if (selectedLocation?.info?.name?.includes(',')) {
              const parts = selectedLocation.info.name.split(',');
              const stateCode = parts[parts.length - 1].trim();
              currentStateName = stateCode.length === 2 ? getFullStateName(stateCode) : stateCode;
            } else if (selectedLocation?.info?.state) {
              currentStateName = selectedLocation.info.state;
            }
            
            const isSelectedState = feature.properties.name === currentStateName || 
                                  getStateAbbreviation(feature.properties.name) === currentStateName;
            const isHovered = hoveredLocation === feature.properties.name;
            
            if (isSelectedState) {
              return {
                weight: 5,
                color: 'var(--navy)',
                fillOpacity: 0,
                opacity: 0.8
              };
            } else {
              return {
                fillColor: 'var(--silver)',
                weight: isHovered ? 5 : 2,
                opacity: 0.8,
                color: 'var(--navy)',
                fillOpacity: 0.6
              };
            }
          }}
          onEachFeature={(feature, layer) => {
            if (isMetro) return;
            
            let currentStateName = null;
            
            if (isAllCountiesInState || isAllCitiesInState) {
              let statePart = selectedLocation.info.name
                .replace(/^All /, '')
                .replace(/ Counties$/, '')
                .replace(/ Cities$/, '');
              currentStateName = statePart.length === 2 ? getFullStateName(statePart) : statePart;
            } else if (selectedLocation?.info?.name?.includes(',')) {
              const parts = selectedLocation.info.name.split(',');
              const stateCode = parts[parts.length - 1].trim();
              currentStateName = stateCode.length === 2 ? getFullStateName(stateCode) : stateCode;
            } else if (selectedLocation?.info?.state) {
              currentStateName = selectedLocation.info.state;
            }
            
            const isSelectedState = feature.properties.name === currentStateName || 
                                  getStateAbbreviation(feature.properties.name) === currentStateName;
            
            if (!isSelectedState) {
              layer.on({
                mouseover: (e) => {
                  if (disableInteractions || isAnyMenuOpen || clickedLayerRef.current || stateHoverPopupLayerRef.current || preventHoverUntilMouseMove.current) return;
                  
                  if (!isMobile && !isTouchDevice && !isTransitioning.current) {
                    setHoveredLocation(feature.properties.name);
                    
                    if (!hoverPopupRef.current) {
                      const popupContainer = document.createElement('div');
                      popupContainer.className = 'leaflet-popup custom-popup hover-popup';
                      popupContainer.style.position = 'fixed';
                      popupContainer.style.zIndex = '1001';
                      popupContainer.style.pointerEvents = 'none';
                      popupContainer.style.transform = 'translate(-50%, -100%)';
                      popupContainer.innerHTML = `
                        <div class="leaflet-popup-content-wrapper">
                          <div class="leaflet-popup-content"></div>
                        </div>
                        <div class="leaflet-popup-tip-container">
                          <div class="leaflet-popup-tip"></div>
                        </div>
                      `;
                      document.body.appendChild(popupContainer);
                      hoverPopupRef.current = popupContainer;
                      hoverPopupContentRef.current = popupContainer.querySelector('.leaflet-popup-content');
                    }
                    
                    hoverPopupContentRef.current.innerHTML = generateStatePopupContent(feature);
                    
                    const mapContainer = map.getContainer();
                    const mapRect = mapContainer.getBoundingClientRect();
                    const mousePoint = e.originalEvent;
                    const popupLatLng = e.target._map.mouseEventToLatLng(mousePoint);
                    const point = map.latLngToContainerPoint(popupLatLng);
                    
                    hoverPopupRef.current.style.left = `${mapRect.left + point.x}px`;
                    hoverPopupRef.current.style.top = `${mapRect.top + point.y - 13}px`;
                    hoverPopupRef.current.style.display = 'block';
                  }
                },
                mousemove: (e) => {
                  if (disableInteractions || isAnyMenuOpen || preventHoverUntilMouseMove.current) return;
                  
                  if (!isMobile && !isTouchDevice && !isTransitioning.current && !clickedLayerRef.current && hoverPopupRef.current) {
                    const mapContainer = map.getContainer();
                    const mapRect = mapContainer.getBoundingClientRect();
                    const mousePoint = e.originalEvent;
                    const popupLatLng = e.target._map.mouseEventToLatLng(mousePoint);
                    const point = map.latLngToContainerPoint(popupLatLng);
                    
                    hoverPopupRef.current.style.left = `${mapRect.left + point.x}px`;
                    hoverPopupRef.current.style.top = `${mapRect.top + point.y - 13}px`;
                  }
                },
                mouseout: (e) => {
                  if (!isMobile) {
                    setHoveredLocation(null);
                    if (!clickedLayerRef.current && !stateHoverPopupLayerRef.current) {
                      hideHoverPopup();
                    }
                  }
                },
                click: (e) => {
                  if (disableInteractions) return;
                  e.originalEvent.stopPropagation();
                  hideHoverPopup();
                  preventHoverUntilMouseMove.current = true;
                  
                  const stateName = feature.properties.name;
                  const stateAbbr = getStateAbbreviation(stateName);

                  if (isCounty && stateName === 'District of Columbia') return;
                  
                  onLocationSelect({
                    name: `All ${stateAbbr} ${isCounty ? 'Counties' : 'Cities'}`,
                    type: isCounty ? 'county' : 'city',
                    info: {
                      name: `All ${stateAbbr} ${isCounty ? 'Counties' : 'Cities'}`,
                      type: isCounty ? 'county' : 'city',
                      state: stateName,
                      stateAbbr: stateAbbr
                    }
                  });
                }
              });
            }
          }}
        />
      )}

      {!(isAllCountiesNationwide || isAllCitiesNationwide) || !locationTypeData.currentState ? (
        <GeoJSON
          key={`geojson-${locationTypeData.type}-${locationTypeData.currentState || 'nationwide'}`}
          ref={layerRef}
          data={features}
          style={getStyleFunction}
        />
      ) : null}
    </>
  );
});

const MapView = ({ 
  selectedView, 
  activeTab,
  isPinned, 
  setIsPinned,
  isFilterOpen,
  setIsFilterOpen, 
  isMobile,
  selectedLocation,
  onLocationSelect,
  selectedCrime,
  setSelectedCrime,
  selectedDateRange,
  setSelectedDateRange,
  selectedSource,
  setSelectedSource,
  selectedData,       
  setSelectedData,
  compareMode,
  onCompareModeChange,
  rateViewMode,
  setRateViewMode,
  getActiveCompareMode,
  mapRange,
  onMapRangeChange,
  hoveredLocation,
  setHoveredLocation,
  setAvailableYears,
  availableYears,
  showCityResolutionModal,
  setShowCityResolutionModal,
  setCityResolutionData,
  yearCheckInProgress,
  onSaveSearch,
  userPlan,
  mostRecentYear,
  closePopupSignal,
  searchBlockedRef,
  mapSearchGateRef,
  exportContext,
  exportContextRef,
  onRequestUpgrade,
}) => {
  const [currentTileLayer, setCurrentTileLayer] = useState({
    url: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
    attribution: '&copy; <a href="https://carto.com/attributions" target="_blank">CARTO</a>, &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors'
  });
  const [isMapMobile, setIsMapMobile] = useState(window.innerWidth <= 767);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const handlePopupOpen = useCallback((state = true) => {
    setPopupOpen(state);
  }, []);
  const [isAnyMenuOpen, setIsAnyMenuOpen] = useState(false);
  const [debouncedCompareMode, setDebouncedCompareMode] = useState(compareMode);
  const [debouncedRateViewMode, setDebouncedRateViewMode] = useState(rateViewMode);
  const [disableInteractionsDuringTransition, setDisableInteractionsDuringTransition] = useState(false);
  
  const [locationTypeData, setLocationTypeData] = useState({
    data: null,
    type: null,
    dateRange: null,
    crime: null,
    source: null,
    currentState: null,
    loading: false,
    error: null
  });

  const [mapStyle, setMapStyle] = useState({
    activeTab,
    crime: selectedCrime,
    range: mapRange
  });

  const mapRef = useRef(null);
  const pendingYearAdjustment = useRef(false);
  const debouncedSetMapStyleRef = useRef();
  const prevCompareMode = useRef(compareMode);
  const isFetching = useRef(false);
  const fetchId = useRef(0);
  const pendingFetchTimeoutRef = useRef(null);
  const lastFetchedLocationNameRef = useRef(null);
  const lastFetchParamsRef = useRef(null);
  const preventHoverUntilMouseMoveRef = useRef(false);
  const lastMousePositionRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const handleResize = () => {
      setIsMapMobile(window.innerWidth <= 767);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  useEffect(() => {
    const checkTouchDevice = () => {
      setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
    };
    
    checkTouchDevice();
    window.addEventListener('resize', checkTouchDevice);
    return () => {
      window.removeEventListener('resize', checkTouchDevice);
    };
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const currentPos = { x: e.clientX, y: e.clientY };
      const lastPos = lastMousePositionRef.current;
      
      const hasMoved = Math.abs(currentPos.x - lastPos.x) > 2 || 
                       Math.abs(currentPos.y - lastPos.y) > 2;
      
      if (hasMoved && preventHoverUntilMouseMoveRef.current) {
        preventHoverUntilMouseMoveRef.current = false;
      }
      
      lastMousePositionRef.current = currentPos;
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, []);
  
  useEffect(() => {
    preventHoverUntilMouseMoveRef.current = true;
    setHoveredLocation(null);
  }, [selectedDateRange, selectedSource, setHoveredLocation]);

  useEffect(() => {
    if (selectedView === 'map') {
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 100);
    }
  }, [selectedView]);

  useEffect(() => {
    debouncedSetMapStyleRef.current = debounce((newStyle) => {
      setMapStyle(newStyle);
    }, 50);
  }, []);

  useEffect(() => {
    if (debouncedSetMapStyleRef.current) {
      debouncedSetMapStyleRef.current({
        activeTab,
        crime: selectedCrime,
        range: mapRange,
      });
    }
  }, [activeTab, selectedCrime, mapRange]);

  useEffect(() => {
    if (prevCompareMode.current !== compareMode) {
      const timer = setTimeout(() => {
        setDebouncedCompareMode(compareMode);
      }, 100);
      
      prevCompareMode.current = compareMode;
      return () => clearTimeout(timer);
    }
  }, [compareMode]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedRateViewMode(rateViewMode);
    }, 100);
  
    return () => clearTimeout(timer);
  }, [rateViewMode]);

  useEffect(() => {
    if (locationTypeData.data && 
        locationTypeData.crime !== selectedCrime &&
        selectedLocation?.info?.type &&
        selectedLocation.info.type !== 'county' &&
        selectedLocation.info.type !== 'city') {
      setLocationTypeData(prev => ({
        ...prev,
        crime: selectedCrime
      }));
    }
  }, [selectedCrime, locationTypeData.data, locationTypeData.crime, selectedLocation?.info?.type]);

  useEffect(() => {
    return () => {
      if (pendingFetchTimeoutRef.current) {
        clearTimeout(pendingFetchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!selectedLocation?.info?.type || !selectedLocation?.info?.name || !selectedLocation.info) return;

    if (pendingFetchTimeoutRef.current) {
      clearTimeout(pendingFetchTimeoutRef.current);
      pendingFetchTimeoutRef.current = null;
    }
  
    const { name: newName, type: newType } = selectedLocation.info;
    const { type: prevType, currentState: prevState } = locationTypeData;
    const prevNameForFetch = lastFetchedLocationNameRef.current;
  
    const isAllCitiesOrCountiesNationwide = (newType === 'city' && newName === 'All Cities') || 
                                         (newType === 'county' && newName === 'All Counties');
    const isAllCountiesInState = newType === 'county' && newName.startsWith('All ') && newName.endsWith(' Counties');
    const isAllCitiesInState = newType === 'city' && newName.startsWith('All ') && newName.endsWith(' Cities');
    let currentState = null;
    
    if (isAllCountiesInState) {
      const statePart = newName.replace(/^All /, '').replace(/ Counties$/, '');
      currentState = statePart.length === 2 ? getFullStateName(statePart) : statePart;
    } else if (isAllCitiesInState) {
      const statePart = newName.replace(/^All /, '').replace(/ Cities$/, '');
      currentState = statePart.length === 2 ? getFullStateName(statePart) : statePart;
    } else if ((newType === 'county' || newType === 'city') && newName.includes(',')) {
      currentState = newName.split(',')[1]?.trim();
    }

    const currentParams = `${newName}-${newType}-${selectedDateRange}-${selectedSource}-${selectedCrime}`;
    if (isFetching.current && lastFetchParamsRef.current === currentParams) {
      return; 
    }
  
    const isBothAllStateViews = prevState && currentState && (isAllCitiesInState || isAllCountiesInState);
    const isAllStateTransition = 
      isBothAllStateViews &&
      newType === prevType &&
      selectedDateRange === locationTypeData.dateRange &&
      selectedSource === locationTypeData.source &&
      selectedCrime === locationTypeData.crime;

    const prevAgg =
      prevNameForFetch &&
      prevType &&
      isStateScopedCityCountyAggregate({
        info: { name: prevNameForFetch, type: prevType },
      });
    const newAgg = isStateScopedCityCountyAggregate({
      info: { name: newName, type: newType },
    });
    const hasLocationNameChanged =
      !prevNameForFetch ||
      prevNameForFetch !== newName ||
      prevType !== newType;
    const isAggregatePeerSwap =
      hasLocationNameChanged &&
      newType === prevType &&
      (newType === 'city' || newType === 'county') &&
      currentState &&
      prevState &&
      String(prevState).toLowerCase() === String(currentState).toLowerCase() &&
      ((prevAgg && !newName.startsWith('All ')) ||
        (newAgg && prevNameForFetch && !prevNameForFetch.startsWith('All ')));
    const shouldDisableInteractionsForTransition =
      isAllStateTransition || isAggregatePeerSwap;

    const canSkipSameMapDataset =
      newType === prevType &&
      ((newType !== 'county' && newType !== 'city') || currentState === prevState) &&
      selectedDateRange === locationTypeData.dateRange &&
      selectedSource === locationTypeData.source &&
      (!(newType === 'county' || newType === 'city') || isAllCitiesOrCountiesNationwide || selectedCrime === locationTypeData.crime);

    if (canSkipSameMapDataset && yearCheckInProgress?.current) {
      yearCheckInProgress.current = false;
    }

    if (canSkipSameMapDataset) {
      if (!isFetching.current || lastFetchParamsRef.current === currentParams) {
        if (!isFetching.current) {
          setShowLoading(false);
        }
        setDisableInteractionsDuringTransition(false);
        return;
      }
    }

    if (yearCheckInProgress?.current) {
      yearCheckInProgress.current = false;
    }

    const currentFetchId = ++fetchId.current;
    lastFetchParamsRef.current = currentParams;
    setDisableInteractionsDuringTransition(shouldDisableInteractionsForTransition);
  
    pendingFetchTimeoutRef.current = setTimeout(() => {
      pendingFetchTimeoutRef.current = null;
      isFetching.current = true;
      preventHoverUntilMouseMoveRef.current = true;
      setHoveredLocation(null);
      setLocationTypeData(prev => ({ ...prev, loading: true }));

      if (!shouldDisableInteractionsForTransition) {
        setShowLoading(true);
      }

      fetchLocationData(
        newName,
        selectedDateRange,
        newType,
        selectedSource,
        true,
        selectedCrime,
        selectedLocation.info?.skipResolutionCheck || false
      )
        .then(({ typeData, requiresCityResolution, cityResolutionData }) => {
          if (currentFetchId !== fetchId.current) return;
              
          if (requiresCityResolution) {
            setCityResolutionData(cityResolutionData);
            setShowCityResolutionModal(true);
            setShowLoading(false);
            setLocationTypeData(prev => ({ ...prev, loading: false }));
            return;
          }
        
          if (typeData?.availableYears && Array.isArray(typeData.availableYears)) {
            const yearExists = typeData.availableYears.some(year => year.toString() === selectedDateRange);
          
            if (!yearExists && typeData.availableYears.length > 0) {
              pendingYearAdjustment.current = true;
              setAvailableYears(typeData.availableYears);
              return;
            }
          
            if (setAvailableYears && newName.startsWith('All ')) {
              setAvailableYears(typeData.availableYears);
            }
          }
        
          pendingYearAdjustment.current = false;

          lastFetchedLocationNameRef.current = newName;

          setLocationTypeData({
            data: typeData,
            type: newType,
            dateRange: selectedDateRange,
            crime: selectedCrime,
            source: selectedSource,
            currentState,
            loading: false,
            error: null
          });
          setDisableInteractionsDuringTransition(false);
          setShowLoading(false);
        })
        .catch(error => {
          if (currentFetchId !== fetchId.current) return;
        
          setLocationTypeData(prev => ({
            ...prev,
            loading: false,
            error: error.message
          }));
          setDisableInteractionsDuringTransition(false);
          setShowLoading(false);
        })
        .finally(() => {
          if (currentFetchId === fetchId.current) {
            isFetching.current = false;
            setDisableInteractionsDuringTransition(false);
          }
        });
    }, 90);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedLocation?.info?.type,
    selectedLocation?.info?.name,
    selectedDateRange,
    selectedSource,
    selectedCrime,
    locationTypeData.type,
    locationTypeData.dateRange,
    locationTypeData.source,
    locationTypeData.crime,
    availableYears,
    setAvailableYears
  ]);

  const handleFilterClose = () => {
    setIsFilterOpen(false);
    if (isPinned !== 'pinned') {
      setIsPinned('unpinned');
    }
  };

  const handleMapRangeChange = (range) => {
    if (onMapRangeChange) {
      onMapRangeChange(range);
    }
  };

  const handleCompareModeChange = ({ type, mode }) => {
    if (type === 'rates') {
      setRateViewMode(mode);
    } else {
      onCompareModeChange(mode);
    }
  };

  const hasPreviousData = useCallback(() => {
    if (activeTab !== 'trends') return true;
    
    return locationTypeData?.data?.locations?.some(location => {
      const prevField = `${mapStyle.crime.toLowerCase()}_count_prev`;
      return location[prevField] !== null && 
             location[prevField] !== undefined &&
             location[prevField] !== 'N/A';
    });
  }, [activeTab, locationTypeData?.data?.locations, mapStyle.crime]);

  const hasData = locationTypeData?.data?.locations?.length > 0;
  const disableMapInteractions = disableInteractionsDuringTransition;

  const zoomLevel = isMapMobile ? 3 : 4;
  const centerLat = isMapMobile ? 37 : 39;
  const centerLong = isMapMobile ? -96 : -97;

  const getComparisonRate = () => {
    const crimePrefix = mapStyle.crime.toLowerCase();
    
    if (mapStyle.activeTab === 'rates' && (selectedLocation?.info?.type === 'county' || selectedLocation?.info?.type === 'city' || selectedLocation?.info?.type === 'metro') && rateViewMode) {
      const rateType = rateViewMode.rateType || 'county';
      const rateScope = rateViewMode.rateScope || 'state';
      
      let avgRateField;
      if (selectedLocation?.info?.type === 'metro') {
        if (rateType === 'metro') {
          avgRateField = `${crimePrefix}_all_metros_rate`;
        } else {
          avgRateField = `${crimePrefix}_national_rate`;
        }
      } else if (rateType === 'county') {
        avgRateField = rateScope === 'state' 
          ? `${crimePrefix}_all_counties_state_rate`
          : `${crimePrefix}_all_counties_national_rate`;
      } else if (rateType === 'city') {
        avgRateField = rateScope === 'state'
          ? `${crimePrefix}_all_cities_state_rate`
          : `${crimePrefix}_all_cities_national_rate`;
      } else {
        avgRateField = rateScope === 'state'
          ? `${crimePrefix}_state_rate`
          : `${crimePrefix}_national_rate`;
      }
      
      const firstLocation = locationTypeData.data?.locations?.[0] || {};
      return firstLocation[avgRateField];
    }
    
    const firstLocation = locationTypeData.data?.locations?.[0] || {};
    return firstLocation[`${crimePrefix}_national_rate`];
  };

  return (
    <div className="map-view-container">
      <div className="map-container">
        {locationTypeData.loading && showLoading && <div className="loading-map"><LoadingOverlay text="map" /></div>}

        <MapContainer 
          ref={mapRef}
          center={[centerLat, centerLong]}
          zoom={zoomLevel}
          minZoom={isMapMobile ? 2 : 3}
          maxBoundsViscosity={1.0}
          maxBounds={[
            [-62, -200],
            [84, 160]
          ]}
        >
          <TileLayer
            attribution={currentTileLayer.attribution}
            url={currentTileLayer.url}
            crossOrigin="anonymous"
          />
          {locationTypeData?.data?.features && (
            <>
              <GeoJSONLayer
                key={`geo-layer-${locationTypeData.type === 'city' || locationTypeData.type === 'county' ? 'city-county' : locationTypeData.type}-${locationTypeData.dateRange || selectedDateRange}-${locationTypeData.crime || selectedCrime}`}
                features={locationTypeData.data.features}
                stateFeatures={locationTypeData.data.stateFeatures}
                mapStyle={mapStyle}
                locationTypeData={locationTypeData}
                onLocationSelect={onLocationSelect}
                selectedLocation={selectedLocation}
                selectedDateRange={selectedDateRange}
                selectedSource={selectedSource}
                rateViewMode={debouncedRateViewMode}
                getActiveCompareMode={getActiveCompareMode}
                hasPreviousData={hasPreviousData()}
                hasData={hasData}
                onPopupOpen={handlePopupOpen}
                isAnyMenuOpen={isAnyMenuOpen}
                hoveredLocation={hoveredLocation}
                setHoveredLocation={setHoveredLocation}
                preventHoverUntilMouseMove={preventHoverUntilMouseMoveRef}
                isMobile={isMobile} 
                isMapMobile={isMapMobile}
                isTouchDevice={isTouchDevice}
                closePopupSignal={closePopupSignal}
                searchBlockedRef={searchBlockedRef}
                mapSearchGateRef={mapSearchGateRef}
                userPlan={userPlan}
                popupOpen={popupOpen}
                disableInteractions={disableMapInteractions}
              />
              {hasData && !showLoading && (
                <>
                  {selectedLocation?.info?.type !== 'nation' && selectedLocation?.info?.name !== 'All Counties' 
                    && selectedLocation?.info?.name !== 'All Cities' && hasPreviousData() && (
                    <GradientLegend 
                      min={getMinMaxValues(
                        locationTypeData.data.locations, 
                        mapStyle.range, 
                        mapStyle.crime.toLowerCase(),
                        mapStyle.activeTab,
                        debouncedCompareMode,
                        debouncedRateViewMode, 
                        selectedLocation?.info?.type
                      ).min}
                      max={getMinMaxValues(
                        locationTypeData.data.locations, 
                        mapStyle.range, 
                        mapStyle.crime.toLowerCase(),
                        mapStyle.activeTab,
                        debouncedCompareMode,
                        debouncedRateViewMode,
                        selectedLocation?.info?.type
                      ).max}
                      activeTab={mapStyle.activeTab}
                      range={mapStyle.range}
                      nationalRate={getComparisonRate()}
                      compareMode={debouncedCompareMode}
                      rateViewMode={debouncedRateViewMode}
                    />
                  )}
                  {isMobile && activeTab === 'reports' && selectedLocation?.info?.name && !selectedLocation.info.name.startsWith('All ') && (
                    <DataSelector
                      selectedData={selectedData}
                      setSelectedData={setSelectedData}
                      locationType={selectedLocation.info.type}
                      popupOpen={popupOpen}
                      variant="map"
                    />
                  )}
                  {!(selectedLocation?.info?.type === 'nation' && activeTab === 'scores') && (
                    <ExportActions 
                      selectedView="map" 
                      selectedLocation={{
                        ...selectedLocation,
                        info: {
                          ...selectedLocation.info,
                          type: !hasPreviousData() ? 'nation' : selectedLocation.info.type
                        }
                      }}
                      popupOpen={popupOpen}
                      onMenuStateChange={setIsAnyMenuOpen}
                      activeTab={activeTab}
                      hasPreviousData={hasPreviousData()}
                      isInsideMapContainer={true}
                      locationName={selectedLocation?.info?.name || ''}
                      exportContext={exportContext}
                      exportContextRef={exportContextRef}
                      userPlan={userPlan}
                      onRequestUpgrade={onRequestUpgrade}
                    />
                  )}
                  {(selectedLocation?.info?.type !== 'nation' || 
                    (selectedLocation?.info?.type === 'nation' && activeTab === 'trends')) && hasPreviousData() && (
                    <MapMinMax
                      selectedLocation={selectedLocation}
                      activeTab={activeTab}
                      locationTypeData={locationTypeData}
                      selectedCrime={selectedCrime}
                      selectedSource={selectedSource}
                      onMinMaxChange={handleMapRangeChange}
                      currentRange={mapRange}
                      popupOpen={popupOpen}
                      onMenuStateChange={setIsAnyMenuOpen}
                      getActiveCompareMode={getActiveCompareMode}
                      rateViewMode={rateViewMode}
                      onCompareModeChange={handleCompareModeChange}
                      onUnifiedCompareModeChange={onCompareModeChange}  
                    />
                  )}
                </>
              )}
            </>
          )}
          <MapLocation 
            onLocationSelect={onLocationSelect} 
            selectedLocation={selectedLocation}
            popupOpen={popupOpen}
            onMenuStateChange={setIsAnyMenuOpen}
          />
          <MapLayer 
            onLayerChange={setCurrentTileLayer} 
            popupOpen={popupOpen}
            onMenuStateChange={setIsAnyMenuOpen}
          />
        </MapContainer>

        {!isMobile && (
          <div className="filters-overlay">
            <Filters 
              isPinned={isPinned}
              setIsPinned={setIsPinned}
              isFilterOpen={isFilterOpen}
              onClose={handleFilterClose}
              selectedLocation={selectedLocation}
              onLocationSelect={onLocationSelect}
              selectedCrime={selectedCrime}
              setSelectedCrime={setSelectedCrime}
              selectedDateRange={selectedDateRange}
              setSelectedDateRange={setSelectedDateRange}
              selectedSource={selectedSource}
              setSelectedSource={setSelectedSource}
              availableYears={availableYears}
              onMenuStateChange={setIsAnyMenuOpen}
              onSaveSearch={onSaveSearch}
              userPlan={userPlan}
              mostRecentYear={mostRecentYear}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MapView;